/* eslint-disable eqeqeq */
/**
 * Phase 6e2c-4 — FILTER selector (§33.7) — THE shim #8 cut.
 *
 * React port of components/filter/view/filter.selector.js (1,849) + its 5
 * templates (filter.selector + filter.selector.output + how_to + pricing.method
 * + rowTemplate), ported into the 6e2c-0+1 component-selector chassis (§30) as a
 * tab body. BOARD only; opened from the React filter FORM's rate-active-mode
 * "Rating" label (FilterForm → RatingModeBlock → api.openRatingStub →
 * rating_mode:activate {name:'filter', current_model:{id:row.id}, section}).
 * The chassis fan-out key is therefore filter_<componentId>:selector:<action>.
 *
 * ── THE shim #8 ONE-CUT (§50.2 row 8) ────────────────────────────────────────
 * Unlike every other selector, the filter selector does NOT clone a staging
 * model — it operates on the LIVE filter form model through window.__FORM_SHIM__
 * [componentId] (the Q15 shim the React FilterForm registers):
 *   shim.rate (#3)              — set TRUE on open / FALSE on close (suppresses
 *                                 the form's §49.2 auto-saves + §49.3 renders
 *                                 while the rating dialog owns the model).
 *   shim.model (#2)             — the live FormModel (get/set/onChange).
 *   shim.catalogueFields[t]     — #5 counts (cms_count/cms_full_count),
 *                                 #6 resetFilter, #8 catalogueSelector seam.
 *   shim.render (#1)            — repaint the form after select/cancel.
 *
 * The legacy DOM surgery re-homed the singleton smart-list table INTO the dialog
 * (catalogueSelector.catalogueRowsContainer = #catalogue-rows-filter-container,
 * saving the originals as self.a/self.m, restoring on close). We do this
 * React-NATIVELY via the explicit `catalogueSelectorSeam`
 * (catalogueField.ts:60): on mount we SAVE the seam's current
 * catalogueRowsContainer/mainRotationElement, then point the seam at the
 * dialog's OWN React-rendered containers + set with_rating=true; on cleanup
 * (cancel OR select) we RESTORE the saved values + with_rating=false so the
 * form's catalogue chrome is whole again (the #1 restore-integrity risk). The
 * smart-list ROWS themselves render React-side from getCatalogPage (no jQuery
 * detach of live nodes).
 *
 * ── DATA-SAFETY (§4 + the 6e2a userArmed gate) ★ writes shared dev3 ──────────
 * OPENING fires ZERO writes. The getFilterData preview round-trip (PATCH
 * ahu/filters/<id> {…, rate:true}) PERSISTS on the server, so it is gated behind
 * a `userArmed` ref — armed ONLY by a real user field/bank/size change. Legacy
 * fires getFilterData on OPEN (initialize) to paint initial outputs; we DO NOT
 * replicate that — initial outputs render from the model's EXISTING stored
 * values (the same principle as the 6e2a userArmed gate; we never save-on-init).
 * Select also writes (current_model PATCH). Both the rate round-trip and the
 * Select apply are CODE-VERIFIED ONLY in smoke, PARKED for human QA on
 * throwaway data.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';
import { useAppDispatch } from 'app/hooks';
import { ahuBoardFormsApi } from 'app/services/ahuBoardFormsApi';
import {
  ahuComponentFormsApi,
  useGetComponentCmsDocQuery,
} from 'app/services/ahuComponentFormsApi';
import { validateOnNewComponent } from '../../validation/validationController';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { formatDecimalDisplay, formatIntegerDisplay } from '../formUtils';
import {
  filterSelectorApi,
  type IFilterSizeQtyRow,
  type IFilterSizeRow,
} from './filterSelectorApi';
import { subscribeFormShims } from '../componentFormTypes';
import type { ComponentFormShim } from '../componentFormTypes';
import type { IMasterRow } from 'app/services/ahuComponentFormsApi';
import type { SelectorTabProps } from './selectorTypes';
import { takeSectionPendingRerate } from '../../psychrometric/pendingRerate';
import { queueSectionPatch } from '../sectionPatchQueue';
import { cmsSeasonsByComponent, deriveSeasonValue } from '../cmsDoc';
import { seasonShowFlags, MIN_AIR_TEMP_F } from '../SeasonGroup';
import type { ICmsAhuDoc } from 'models';

const SIZES_QTY_BASE = '/api/v1.0/master/v1.0.0/ahu/filter_sizes_and_quantities/';
const LOADINGS_URL = '/api/v1.0/cms/1/v1.0.0/ahu/filters/loadings/';
/** legacy loadParams (filter.selector.js:1871-1886) — the four Frame param
 *  lists live under cms/.../ahu/filters/frames/<name>/ (urls_filters.py:97-100,
 *  each answering {"<name>": [{id, name|series}, …]}). */
const FRAME_PARAMS_BASE = '/api/v1.0/cms/1/v1.0.0/ahu/filters/frames/';
/** getFrameParamas' frame_fields map (filter.selector.js:1929-1934). */
const FRAME_FIELDS = {
  mfg_id: 'mfgs',
  series_id: 'series',
  frame_type_id: 'frame_types',
  mtl_id: 'mtls',
} as const;
/** GLOBAL cms doc (legacy AHUCms — air.season/season_by_component). */
const GLOBAL_CMS_URL = '/api/v1.0/cms/1/v1.0.0/ahu/?construction_class_id=1';

/**
 * filter.selector.js:249 — the model fields whose BACK-END change means the
 * sizer's APPLY landed on this filter. Verbatim; `airflow_actual`,
 * `max_different_sizes` and `selection_condition` move with them but are not
 * part of the trigger there either.
 */
const SIZER_APPLY_FIELDS = [
  'allowable_filter_sizes',
  'min_face_velocity',
  'max_face_velocity',
  'size_qty_id',
] as const;

/** …and its coalescing window (js:250/288 `self.sizer_timer`). */
const SIZER_RESEED_DEBOUNCE_MS = 300;

/** J6 — 'performance' joins the input tabs (filter.selector.tmpl:15-17,
 * between Mounting and Pricing). */
type InputTab = 'filter' | 'frame' | 'mounting' | 'performance' | 'pricing';
/** The OUTPUT nav has the same three panes as legacy — filter.selector.output
 *  .tmpl:5-16 renders Filter / Frame / Performance, and activateOutputLabel
 *  (filter.selector.js:1264-1275) resolves `href + "_output"`, so
 *  `#ahu-Filter_Performance_output` (output.tmpl:311) IS reachable. */
type OutputTab = 'filter' | 'frame' | 'performance';

const parseJSON = (raw: unknown): Record<string, unknown> => {
  if (raw == null || raw === '') return {};
  try {
    return JSON.parse(String(raw)) as Record<string, unknown>;
  } catch {
    return {};
  }
};

const num = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
};

/** data-min / data-max / data-step carried by an `input[data-type="number"]`. */
interface NumFieldAttrs {
  min?: unknown;
  max?: unknown;
  step?: unknown;
}

/**
 * validateNumberField (filter.selector.js:1422-1473), INPUT side.
 *
 * Legacy runs it over every `input[data-type="number"]` — from validateNumbers
 * (js:1413-1420, the 500 ms post-render sweep) and from the head of
 * changeModelField (js:595-597). The React port clamped only where a call site
 * happened to remember to, and never applied the display formatting, so e.g.
 * Airflow rendered `12000` instead of `12,000` and Mounting Angle accepted 500.
 *
 * Verbatim, INCLUDING the two legacy quirks:
 *  - js:1425-1426 `input.data("min") ? Number(...) : -Infinity` — a data-min of
 *    ZERO is FALSY, so a 0 floor is NOT enforced (Air PD Final's data-min="0").
 *  - js:1427 `input.data("step") == 0.01 ? "decimal" : "integer"` — ONLY an
 *    explicit 0.01 step is decimal; a missing step rounds to a whole number.
 * js:1434 skips the whole clamp/format pass for an empty input.
 *
 * Deviation: legacy re-reads `e.target.value` AFTER rewriting it (js:596-598),
 * so its model ends up holding the comma-formatted STRING ("1,200") and every
 * consumer strips the comma again (js:756). We keep the model numeric and only
 * the DISPLAY formatted — same settled screen, no comma strings in the payload.
 */
const numberField = (
  raw: string,
  { min, max, step }: NumFieldAttrs = {}
): { value: number | null; display: string } => {
  if (raw === '') return { value: null, display: '' };
  // js:1424 — `Number(val.replace(',','')) || 0`: junk becomes 0.
  let v = Number(String(raw).replace(/,/g, '')) || 0;
  const lo = min ? Number(min) : -Infinity;
  const hi = max ? Number(max) : Infinity;
  const decimal = Number(step) === 0.01;
  // js:1429-1433
  v = decimal ? Number(v.toFixed(2)) : Math.round(v);
  if (v > hi) v = hi; // js:1435-1442
  if (v < lo) v = lo; // js:1443-1450
  // js:1451-1453 — fn.uni_tools.formatNumberWithCommaSeparator.
  return {
    value: v,
    display: decimal ? formatDecimalDisplay(v) : formatIntegerDisplay(v),
  };
};

/**
 * The `change .model_field` half of the above: clamp + format, write the
 * formatted form straight back into the input (js:1437/:1445/:1453), then hand
 * the clamped value to changeModelField (js:595-598).
 */
const onNumberBlur =
  (
    name: string,
    attrs: NumFieldAttrs,
    onModelField: (n: string, raw: string | boolean) => void
  ) =>
  (e: React.FocusEvent<HTMLInputElement>) => {
    const d = numberField(e.target.value, attrs);
    e.currentTarget.value = d.display;
    onModelField(name, d.value == null ? '' : String(d.value));
  };

/** legacy `mfg` — the active manufacturer id used for cms_id in the size URL. */
const activeMfgId = (): number => {
  const w = window as unknown as { _STORAGE_?: { mfg?: { id?: number } } };
  return Number(w._STORAGE_?.mfg?.id) || 1;
};

/**
 * The selector body. Mounted by <FilterSelector> ONLY once the live form shim
 * exists — every one of its init effects reads `getShim()` and bails when the
 * shim is missing, and none of them re-run when it later appears, so mounting
 * early yielded a permanently half-initialised dialog (the stuck preloader).
 */
const FilterSelectorBody = ({
  modelId,
  activate,
  setSelectEnabled,
  showNotes: _showNotes,
}: SelectorTabProps) => {
  const dispatch = useAppDispatch();

  // ---- the LIVE form shim (the filter form registered it) ----
  const getShim = useCallback(
    (): ComponentFormShim | undefined => window.__FORM_SHIM__?.[modelId],
    [modelId]
  );

  // ---- read-only collections (fire on open; never mutate) ----
  const { data: filterSizes = [] } =
    filterSelectorApi.useGetFilterSizesQuery();
  const { data: selectionCondition = [] } =
    filterSelectorApi.useGetFilterSelectionConditionQuery();
  const { data: pricingMethods = [] } =
    filterSelectorApi.useGetFilterPricingMethodsQuery();
  // legacy self.collections = fn['current.filter.cms'].toJSON().filter — SAME
  // URL the form's componentCms loads (cms/1/v1.0.0/ahu/filters/params/),
  // unwrapped under the "filter" key.
  const { data: rawFilterCms } = useGetComponentCmsDocQuery(
    '/api/v1.0/cms/1/v1.0.0/ahu/filters/params/'
  );
  const collections = useMemo<
    Record<string, Array<Record<string, unknown>>>
  >(() => {
    if (!rawFilterCms) return {};
    const inner = (rawFilterCms.filter ?? rawFilterCms) as Record<
      string,
      unknown
    >;
    return inner as Record<string, Array<Record<string, unknown>>>;
  }, [rawFilterCms]);
  /**
   * The ONLY UL Class this catalogue offers, when it offers exactly one.
   *
   * The UL Class select is the one `.params_filter` in the Selection pane with
   * no `- -` option (tmpl / FilterParamsPanel below), so a browser always
   * paints its first option — and with a single-option list that paint is a
   * promise the filter does not keep: `filter_params.ul_class_id` can be unset
   * while the user reads "2" off the screen. Reset is where the two part
   * company (`resetCatalogueFor`), so that is where the shown value is written
   * back into the filter. `undefined` when the list has 0 or 2+ entries —
   * with a real choice on offer, the select's blank-ish state is honest and
   * picking for the user would be inventing a constraint.
   */
  const soleUlClassId = useMemo<unknown>(() => {
    const classes = collections.class;
    return Array.isArray(classes) && classes.length === 1
      ? classes[0].id
      : undefined;
  }, [collections]);
  // legacy self.loadings = getLoadings(TRUE) — filter.selector.js:161, the
  // dependency-FREE call whose result is the select's full option list
  // (js:827 → tmpl). The dependency-FILTERED variant (js:1832-1869) is
  // refreshLoadings below; it narrows THIS list rather than replacing it,
  // exactly as changeSelectOptions does. Read-only.
  const { data: loadings = [] } = filterSelectorApi.useGetFilterLoadingsQuery(
    LOADINGS_URL
  );
  // J6 — the GLOBAL cms doc (legacy AHUCms): season_by_component.Filter feeds
  // the Performance tab's Season select (RTKQ-cached across selectors).
  const { data: globalDoc } = useGetComponentCmsDocQuery(GLOBAL_CMS_URL);

  const [, forceRender] = useState(0);
  const bump = useCallback(() => forceRender((v) => v + 1), []);

  // userArmed gate: the getFilterData preview PATCH fires ONLY after a real
  // user change. Nothing on mount / open / tab-switch arms it (data-safety).
  const userArmed = useRef(false);
  const selectPressed = useRef(false);
  // Dev-sync J6 — sectionDraft (filter.selector.js:97): season/EADB/EAWB live
  // on the SECTION, staged on a detached copy (a .set() on the live shared
  // section model would autosave-PATCH immediately); persisted ONLY by
  // selectFilter's section_save_data (js:774-796).
  const sectionDraft = useRef<Record<string, unknown>>({});
  // init snapshot of the live model (legacy self.init = {...model.toJSON()}).
  const init = useRef<Record<string, unknown>>({});
  // shim #8 saved seam originals (legacy self.a / self.m).
  const savedRowsContainer = useRef<HTMLElement | null>(null);
  const savedRotation = useRef<HTMLElement | null>(null);
  const seamRestored = useRef(false);

  // active input / output tabs (legacy .input-nav-item.selected / output).
  const [inputTab, setInputTab] = useState<InputTab>('filter');
  const [outputTab, setOutputTab] = useState<OutputTab>('filter');
  // Filter-tab sub-tabs (legacy .more_tabing: Selection | Conditions).
  const [filterSubTab, setFilterSubTab] = useState<'selection' | 'conditions'>('selection');
  // smart-list visibility per side (legacy #filter_smart_list / #frame_smart_list).
  const [smartList, setSmartList] = useState<null | 'filter' | 'frame'>(null);
  // `animate-rotate` window (catalogue_selector.js:232-239 / :253-261) — the
  // class rides on BOTH the list and its sibling for exactly 50ms. See
  // `runRotate` below for the whole choreography.
  const [rotating, setRotating] = useState(false);
  const rotateTimer = useRef<number | undefined>(undefined);
  const [smartRows, setSmartRows] = useState<IMasterRow[]>([]);
  // which side `smartRows` was fetched for, so re-opening the other side does
  // not paint one list's rows under the other's column headings.
  const smartRowsSide = useRef<null | 'filter' | 'frame'>(null);
  // Is a page request in flight? Legacy paints `#list-preloader` inside the rows
  // container only for the duration of `load_page` and removes it the moment the
  // promise resolves, whatever it returned (catalogue_selector.js:138-143) — so
  // an empty page ends as an EMPTY LIST, not a spinner. Keying the preloader on
  // `!rows.length` instead spun forever whenever the count was 0.
  const [smartLoading, setSmartLoading] = useState(false);
  // guards against a stale fetch clearing the flag for a newer one (open filter,
  // close, open frame before the first page lands).
  const smartFetchSeq = useRef(0);
  const [howToOpen, setHowToOpen] = useState(false);
  // allowable-sizes multiselect popup (legacy .allowable_filter_sizes_list.active).
  const [sizesPopupOpen, setSizesPopupOpen] = useState(false);

  // bank-configuration list state (legacy filterSizesAndQuantities + sizes_page).
  const [bankRows, setBankRows] = useState<IFilterSizeQtyRow[]>([]);
  // legacy self.bank_by_id (js:178) — the saved-but-out-of-list configuration,
  // pinned at the head of the list as `selected non-clickable`.
  const [bankById, setBankById] = useState<IFilterSizeQtyRow | null>(null);
  const sizesPage = useRef(1);
  const sizesLoading = useRef(false);
  // allowable filter sizes (legacy self.allowable_filter_sizes — derived once).
  const allowableSizes = useRef<IFilterSizeRow[]>([]);
  const changedAllowable = useRef<IFilterSizeRow[]>([]);
  // armed by the sizer re-seed; consumed once the replaced rows have painted.
  const scrollToSelectedBank = useRef(false);

  // ── changeSelectOptions narrowing (filter.selector.js:1888-1917) ──────────
  // Legacy does not rebuild these selects; it HIDES the options the current
  // dependencies rule out (`option.addClass('hide')`) and unhides the survivors.
  // `null` = no narrowing pass has landed yet ⇒ everything visible, as before.
  /** frame_loading_id survivors (js:302-305). */
  const [visibleLoadingIds, setVisibleLoadingIds] =
    useState<ReadonlySet<string> | null>(null);
  /** the four Frame param selects' survivors (js:1944-1947). */
  const [visibleFrameParams, setVisibleFrameParams] = useState<
    Record<string, ReadonlySet<string>>
  >({});
  /** changeDepth's mounting_angle lock (js:1961). */
  const [mountingAngleLocked, setMountingAngleLocked] = useState(false);

  // The number inputs stay UNCONTROLLED (legacy commits on change/blur, and a
  // controlled value would fight free typing), so a PROGRAMMATIC rewrite has to
  // repaint them via `key`. Keying on the model value covers the cross-field
  // writes, but not a clamp that lands back on the value already stored — e.g.
  // max_different_sizes 3 → user types 9 → clamped back to 3 (js:659-661), or
  // distance_from_section_ups already sitting on its floor (js:603-607). Legacy
  // rewrites the box regardless (`$(e.target).val(...)`), so bump this nonce
  // for the affected field and the key changes anyway.
  const numRepaint = useRef<Record<string, number>>({});
  const repaintNumber = useCallback((name: string) => {
    numRepaint.current[name] = (numRepaint.current[name] ?? 0) + 1;
  }, []);

  // ---- DOM refs for the relocated containers (shim #8 target nodes) ----
  const filterRowsRef = useRef<HTMLUListElement | null>(null);
  const frameRowsRef = useRef<HTMLUListElement | null>(null);
  const bankUlRef = useRef<HTMLUListElement | null>(null);

  // -------------------------------------------------------------------------
  // open: rate = true + init snapshot + shim #8 relocate (ONCE per mount)
  // -------------------------------------------------------------------------
  useEffect(() => {
    const shim = getShim();
    if (!shim) return undefined;
    // legacy initialize: filterView.rate = true; self.init = {...toJSON()}.
    shim.rate = true;
    init.current = { ...shim.model.toJSON() };

    // Dev-sync J5+J6 — sectionDraft seed (filter.selector.js:97) + the
    // chart's pending re-rate handoff (js:105-125): section-scoped gate;
    // the bare entering pair renames to `_summer` UNCONDITIONALLY (a
    // both-seasons-stale payload carries the bare pair AND the _winter
    // pair), merges onto the draft, and the dialog lands on the
    // Performance input tab (the `_switchToPerformanceOnRender` flag —
    // filter:866 clears it correctly; here the tab state sets directly).
    sectionDraft.current = {
      ...((activate.section as Record<string, unknown> | undefined) ?? {}),
    };
    const pendingFields = takeSectionPendingRerate(
      (activate.section as { id?: number } | undefined)?.id
    );
    if (pendingFields) {
      const f = pendingFields as Record<string, number | undefined>;
      if (f.eadb_temp !== undefined) {
        f.eadb_temp_summer = f.eadb_temp;
        f.eawb_temp_summer = f.eawb_temp;
        delete f.eadb_temp;
        delete f.eawb_temp;
      }
      Object.assign(sectionDraft.current, f);
      setInputTab('performance');
    }

    // derive allowable_filter_sizes from the model (legacy initialize) once
    // the size catalogue is in.
    const m = shim.model;
    const raw = m.get<string>('allowable_filter_sizes');
    if (raw && isValidJSON(raw)) {
      allowableSizes.current = JSON.parse(raw) as IFilterSizeRow[];
    } else if (raw) {
      const list = String(raw).split(',');
      allowableSizes.current = filterSizes.filter((el) =>
        list.includes(el.value)
      );
    } else {
      allowableSizes.current = filterSizes.filter((el) =>
        [1, 2, 7, 8, 9, 10].includes(el.id)
      );
    }
    changedAllowable.current = [...allowableSizes.current];

    // ── shim #8: relocate the form's catalogue smart-list seam into the dialog
    //    (legacy render(): save self.a/self.m, point catalogueRowsContainer at
    //    #catalogue-rows-filter-container, with_rating = true). React-native:
    //    point the seam at our OWN dialog containers; rows render React-side.
    seamRestored.current = false;
    Object.values(shim.catalogueFields).forEach((field) => {
      const seam = field.catalogueSelector;
      if (savedRowsContainer.current == null)
        savedRowsContainer.current = seam.catalogueRowsContainer;
      if (savedRotation.current == null)
        savedRotation.current = seam.mainRotationElement;
      seam.with_rating = true;
      seam.catalogueRowsContainer = filterRowsRef.current;
      seam.mainRotationElement = null;
    });

    // chassis #select gate: legacy checkSelectButtonAvabialty governs it from
    // counts. We enable Select when the live model already has a master pick
    // (so the dialog opens in a "selectable" state for already-rated filters);
    // it does NOT arm userArmed and does NOT mark the chassis dirty.
    setSelectEnabled(!!shim.model.get('master_filter_id'));

    return () => {
      // ── shim #8 RESTORE (legacy cancelChanges / selectFilter): put the seam
      //    back so the FORM's catalogue chrome is whole again. Runs on unmount
      //    whether we leave via Cancel or Select.
      restoreSeam();
    };
    // run once per mount (modelId stable for the dialog's lifetime); filterSizes
    // refresh re-derives allowable below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId]);

  // re-derive allowable once the size catalogue resolves after mount.
  useEffect(() => {
    const shim = getShim();
    if (!shim || !filterSizes.length || allowableSizes.current.length) return;
    const raw = shim.model.get<string>('allowable_filter_sizes');
    if (raw && isValidJSON(raw)) {
      allowableSizes.current = JSON.parse(raw) as IFilterSizeRow[];
    } else if (raw) {
      const list = String(raw).split(',');
      allowableSizes.current = filterSizes.filter((el) => list.includes(el.value));
    } else {
      allowableSizes.current = filterSizes.filter((el) =>
        [1, 2, 7, 8, 9, 10].includes(el.id)
      );
    }
    changedAllowable.current = [...allowableSizes.current];
    bump();
  }, [filterSizes, getShim, bump]);

  // selection_condition default (filter.selector.js:174) — legacy seeds the
  // FIRST condition onto the model when it has none, BEFORE the first
  // getFilterSizesAndQuantities, so the bank list is queried with a dimensional
  // constraint from the very first open. The port left the select blank and
  // dropped `selection_condition` (+ airway_height/airway_width) from the URL.
  // Local set only — the §49.2 pipeline is suppressed while shim.rate is true
  // and the model is not user-armed, so this writes nothing.
  // Declared ABOVE the initial bank-list effect so it runs first on the render
  // where both dependencies land (effects fire in declaration order).
  useEffect(() => {
    const shim = getShim();
    if (!shim || !selectionCondition.length) return;
    if (shim.model.get('selection_condition')) return;
    shim.model.set(
      { selection_condition: selectionCondition[0]?.value },
      { silent: true }
    );
    bump();
  }, [selectionCondition, getShim, bump]);

  const restoreSeam = useCallback(() => {
    if (seamRestored.current) return;
    seamRestored.current = true;
    const shim = getShim();
    if (!shim) return;
    Object.values(shim.catalogueFields).forEach((field) => {
      const seam = field.catalogueSelector;
      seam.catalogueRowsContainer = savedRowsContainer.current;
      seam.mainRotationElement = savedRotation.current;
      seam.with_rating = false;
    });
    shim.rate = false;
    shim.render();
  }, [getShim]);

  // -------------------------------------------------------------------------
  // bank configurations (filter_sizes_and_quantities) — read-only loads
  // -------------------------------------------------------------------------
  const buildSizesUrl = useCallback(
    (page: number, id?: number | string | null, initLoad = false): string => {
      const shim = getShim();
      const m = shim?.model;
      if (!m) return SIZES_QTY_BASE;
      const maxSizes = m.get('max_different_sizes');
      const arrId = m.get('arrangement_id');
      const minV = m.get('min_face_velocity');
      const maxV = m.get('max_face_velocity');
      const selCond = m.get('selection_condition');
      const hFromFloor = m.get('height_from_floor');
      const centerHigh = m.get('center_high');
      const frameLoadingId = m.get('frame_loading_id');
      const hasFrame = m.get('has_frame');
      const mountingAngle = num(m.get('mounting_angle')) ?? 0;
      const airflow = num(m.get('airflow_actual'));
      const sectionH = num(activate.section?.aw_height) ?? sectionDims().h;
      const sectionW = num(activate.section?.aw_width) ?? sectionDims().w;

      let url =
        `${SIZES_QTY_BASE}?page=${page}&cms_id=${activeMfgId()}` +
        `&max_different_sizes=${maxSizes}&arr_id=${arrId}` +
        `&mounting_angle=${mountingAngle}`;
      if (initLoad && id) return `${SIZES_QTY_BASE}?page=1&id=${id}`;
      if (hasFrame) url += `&frame=true`;
      if (frameLoadingId) url += `&frame_loading_id=${frameLoadingId}`;
      if (!centerHigh) url += `&height_from_floor=${hFromFloor}`;
      if (id) url += `&id=${id}`;
      if (allowableSizes.current.length) {
        url += `&allowable_filter_sizes=${allowableSizes.current
          .map((el) => el.value)
          .join(',')}`;
      }
      if (airflow)
        url += `&min_max_velocity=${minV},${maxV}&airflow=${airflow}`;
      if (selCond) {
        url += `&selection_condition=${selCond}`;
        if (selCond !== 'show_all_configurations')
          url += `&airway_height=${sectionH}&airway_width=${sectionW}`;
      }
      return url;
    },
    [getShim, activate.section]
  );

  const fetchSizes = useCallback(
    async (page: number, id?: number | string | null, initLoad = false) => {
      try {
        return await dispatch(
          filterSelectorApi.endpoints.getFilterSizesAndQuantities.initiate(
            buildSizesUrl(page, id, initLoad),
            { subscribe: false }
          )
        ).unwrap();
      } catch {
        return [];
      }
    },
    [dispatch, buildSizesUrl]
  );

  // initial bank list (read-only — runs on open, NO rate PATCH).
  useEffect(() => {
    // js:156-175 — legacy waits for `current.filter.selection.condition` and
    // seeds selection_condition BEFORE getFilterSizesAndQuantities, so the
    // first bank query already carries the constraint. Wait for it here too.
    const shim = getShim();
    if (!shim || !filterSizes.length || !selectionCondition.length) return;
    let cancelled = false;
    void (async () => {
      sizesPage.current = 1;
      const rows = await fetchSizes(1);
      if (cancelled) return;
      const list = Array.isArray(rows) ? rows : [];
      setBankRows(list);
      // legacy initialize (js:176-180): when the model has a saved
      // size_qty_id + master_filter_id that is NOT in the fetched page, it
      // re-fetches that one row by id and PINS it at the head of the list as
      // `selected non-clickable` (tmpl:42-45, data-index="-1"). Without it a
      // filter whose saved bank configuration is no longer valid under the
      // current conditions shows NOTHING selected.
      const savedId = shim.model.get('size_qty_id');
      if (savedId && shim.model.get('master_filter_id') &&
          !list.some((r) => r.id == savedId)) {
        const byId = await fetchSizes(1, num(savedId), true);
        if (cancelled) return;
        const pinned = Array.isArray(byId) ? byId[0] ?? null : null;
        setBankById(pinned);
        if (cancelled) return;
        seedNominalsFromSelection(list, pinned);
        return;
      }
      setBankById(null);
      if (cancelled) return;
      // ── auto-select the first row when nothing is saved ──────────────────
      // Legacy's open-time timer (js:880-894) clicks `li.selected` when there
      // is one, and `li:nth-child(1)` + `.filter_has_changes.active` on its
      // `not_exist` branch. Requested behaviour (2026-08-31): do the same for
      // a NULL bank configuration — `bank_configuration_id` is the form's name
      // for `size_qty_id` (FilterForm.tsx:53).
      //
      // ★ This is a real USER-equivalent selection, so unlike everything else
      // on the open path it DOES write: markChanged() + a non-silent set +
      // the getFilterData rate PATCH, exactly as a click would. That is what
      // makes Available Width x Height populate on open like dev. Deliberate,
      // and scoped to the null case only — a saved configuration still opens
      // write-free through seedNominalsFromSelection below.
      if (!shim.model.get('size_qty_id') && list.length) {
        const first = list[0];
        await selectBankConfig(first.id, first.bank_width, first.bank_height, true);
        if (cancelled) return;
        const li = bankUlRef.current?.querySelector<HTMLLIElement>('li');
        li?.scrollIntoView();
        return;
      }
      seedNominalsFromSelection(list);
    })();
    return () => {
      cancelled = true;
    };
    // re-run when the size catalogue first arrives (open-time only — subsequent
    // refetches go through reloadSizes after a user change).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSizes.length, selectionCondition.length, modelId]);

  // reloadSizes — after a user change (armed) re-pull the bank list.
  // legacy getFilterSizesAndQuantities (js:1664-1696) also RECONCILES: it
  // clears the placement drawing, and if the current size_qty_id is gone from
  // the new list it falls back to the first row (or, on an empty list, nulls
  // size_qty_id + master_filter_id). The port only swapped the rows, so a
  // tightened condition could leave size_qty_id pointing at a row that no
  // longer exists — nothing selected, and the previous drawing still on screen.
  const reloadSizes = useCallback(async () => {
    const shim = getShim();
    sizesPage.current = 1;
    const rows = await fetchSizes(1);
    const list = Array.isArray(rows) ? rows : [];
    setBankRows(list);
    setBankById(null);
    if (shim) {
      const curId = shim.model.get('size_qty_id');
      if (!list.length) {
        shim.model.set({ size_qty_id: null, master_filter_id: null });
      } else if (!list.some((r) => r.id == curId)) {
        shim.model.set({ size_qty_id: list[0].id });
      }
    }
    seedNominalsFromSelection(list);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSizes, getShim]);

  // -------------------------------------------------------------------------
  // SIZER APPLY re-seed — filter.selector.js:249-289 (+ header.js:363-368)
  // -------------------------------------------------------------------------
  // The sizer's APPLY writes THIS filter from the `/sizer/` tab
  // (`sections/<id>/sizer_apply/`): bank configuration, allowable sizes, the
  // face-velocity window, max different sizes, selection condition and airflow.
  // Nothing this tab requested carries it back — it arrives as a syncdata
  // `put ahufilter` frame, which `useAhuBoardSyncdata` sets onto the live model
  // with `__from_back_end`. Legacy listens for exactly that here and re-seeds
  // the OPEN dialog; without it the dialog kept its pre-APPLY values until a
  // full page reload.
  //
  // Most of legacy's body is DOM surgery we get for free. It copies the six
  // fields onto its `current_model` — we have no staging clone (§33.7 shim #8),
  // so the model syncdata set is already the copy — then rewrites the four
  // number inputs and the condition select by hand, which here is the `key`
  // repaint those uncontrolled inputs already do off `model.<field>`. What is
  // left is the state legacy keeps OUTSIDE the model: the allowable-sizes
  // checkbox/label set, and the bank-configuration list, which has to be
  // re-queried because every one of these fields is a parameter of its URL.
  //
  // `init.current` is legacy's `self.init = data` (header.js:363-368 →
  // filter.selector.js:247): the snapshot Select diffs against and Cancel
  // restores. It has to move with the server row or a later Cancel would revert
  // the APPLY, and Select would re-send fields the sizer already saved. Legacy
  // updates it on ANY `put ahufilter`, not just a sizer-shaped one, so the
  // guard below is deliberately looser than the re-seed's.
  useEffect(() => {
    const shim = getShim();
    if (!shim) return undefined;
    let timer: number | undefined;
    const off = shim.model.onChange((changed, info) => {
      if (!info.__from_back_end) return;
      init.current = { ...shim.model.toJSON() };
      if (!SIZER_APPLY_FIELDS.some((field) => field in changed)) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const m = shim.model;
        // js:262 — an apply that left the bank configuration null is not one
        // the dialog can re-seed from.
        if (!m.get('size_qty_id')) return;
        // js:263-266 — the sizer sends allowable_filter_sizes as the comma
        // string, so only the non-JSON branch of the open-time derivation is
        // re-run (a JSON value is what the DIALOG itself writes on Select).
        const raw = m.get<string>('allowable_filter_sizes');
        if (raw && !isValidJSON(raw)) {
          const list = String(raw).split(',');
          allowableSizes.current = filterSizesRef.current.filter((el) =>
            list.includes(el.value)
          );
          changedAllowable.current = [...allowableSizes.current];
        }
        // js:288 `getFilterSizesAndQuantities(true, false, true)` — re-pull the
        // bank list under the new parameters. The `init` argument is what makes
        // it NOT fall back to the first row when the saved configuration is
        // missing from the page, so this is the open-time pin-by-id path rather
        // than `reloadSizes` (which reconciles, and would move the very
        // size_qty_id the sizer just applied).
        void (async () => {
          sizesPage.current = 1;
          const rows = await fetchSizes(1);
          const list = Array.isArray(rows) ? rows : [];
          setBankRows(list);
          const savedId = m.get('size_qty_id');
          const pinned = list.some((r) => r.id == savedId)
            ? null
            : (((await fetchSizes(1, num(savedId), true)) as IFilterSizeQtyRow[])[0] ??
              null);
          setBankById(pinned);
          seedNominalsFromSelection(list, pinned);
          // js:1693-1695 — the applied row is somewhere in a 50-row list that
          // was just replaced, so bring it into view. The <li> does not exist
          // until `setBankRows` paints; the effect below does it after.
          scrollToSelectedBank.current = true;
          bump();
        })();
      }, SIZER_RESEED_DEBOUNCE_MS);
    });
    return () => {
      window.clearTimeout(timer);
      off();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, getShim, fetchSizes, bump]);

  // infinite scroll (legacy scrollSizes — 100 px threshold, 50/page).
  const onBankScroll = useCallback(
    async (e: React.UIEvent<HTMLUListElement>) => {
      if (sizesLoading.current) return;
      const el = e.currentTarget;
      const bottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
      if (bottom < 100 && bankRows.length > 0 && bankRows.length % 50 === 0) {
        sizesLoading.current = true;
        sizesPage.current += 1;
        const more = await fetchSizes(sizesPage.current, undefined, false);
        setBankRows((prev) => [...prev, ...(Array.isArray(more) ? more : [])]);
        sizesLoading.current = false;
      }
    },
    [bankRows.length, fetchSizes]
  );

  // -------------------------------------------------------------------------
  // ★ getFilterData (rate preview round-trip) — userArmed-gated, CODE-ONLY.
  //   PATCH ahu/filters/<id> {…, rate:true} → server outputs → re-render.
  //   PERSISTS on the server. NEVER fired on open (initial outputs come from
  //   the model's stored values).
  // -------------------------------------------------------------------------
  const ratePreviewInFlight = useRef(false);
  const getFilterData = useCallback(async () => {
    const shim = getShim();
    if (!shim || !shim.rate) return;
    // DATA-SAFETY: the rate round-trip writes shared dev3 — never unless a real
    // user change armed it (mirrors 6e2a's save-on-init prohibition).
    if (!userArmed.current) return;
    const m = shim.model;
    const model = m.toJSON();
    const airflowActual = num(model.airflow_actual);
    const payload: Record<string, unknown> = {
      filter_params: model.filter_params,
      frame_params: model.frame_params,
      master_filter_id: model.master_filter_id,
      master_frame_id: model.master_frame_id,
      arrangement_id: model.arrangement_id,
      airflow_actual: airflowActual,
      center_high: model.center_high,
      final_recommended_apd: finalApdChanged.current
        ? (model.final_recommended_apd ?? null)
        : undefined,
      size_qty_id: model.size_qty_id ? Number(model.size_qty_id) : null,
      rate: true,
      frame_loading_id: model.frame_loading_id || null,
      height_from_floor: !model.center_high ? model.height_from_floor : undefined,
      mounting_angle: num(model.mounting_angle) ?? 0,
      distance_from_section_ups: model.distance_from_section_ups || 0,
    };
    ratePreviewInFlight.current = true;
    try {
      const data = (await dispatch(
        filterSelectorApi.endpoints.patchFilterRate.initiate({
          id: modelId,
          data: payload,
        })
      ).unwrap()) as Record<string, unknown>;
      finalApdChanged.current = false;
      // write computed outputs back into the live model (xhr echo — no re-save).
      m.set(
        {
          filter_frame: data.filter_frame,
          initial_apd: data.initial_apd,
          qty_12_24: data.qty_12_24,
          qty_16_20: data.qty_16_20,
          qty_16_24: data.qty_16_24,
          qty_20_16: data.qty_20_16,
          qty_20_20: data.qty_20_20,
          qty_20_24: data.qty_20_24,
          qty_24_12: data.qty_24_12,
          qty_24_16: data.qty_24_16,
          qty_24_20: data.qty_24_20,
          qty_24_24: data.qty_24_24,
          height_from_floor: data.center_high
            ? (data.height_from_floor as number)?.toFixed?.(2)
            : model.height_from_floor,
          face_velocity_actual: data.face_velocity_actual,
          final_recommended_apd: data.final_recommended_apd,
          distance_from_section_ups: model.distance_from_section_ups,
          height: data.height,
        },
        { xhr: true }
      );
      availableWidthHeight.current = data.avaliable_width_height as
        | { available_width?: number; available_height?: number }
        | undefined;
      bump();
      drawBankPlacement();
    } catch (err) {
      // legacy: console.error('getFilterData failed', err)
      // eslint-disable-next-line no-console
      console.error('[filter selector] getFilterData failed', err);
    } finally {
      ratePreviewInFlight.current = false;
    }
  }, [getShim, dispatch, modelId, bump]);

  const finalApdChanged = useRef(false);
  const availableWidthHeight = useRef<
    { available_width?: number; available_height?: number } | undefined
  >(undefined);

  // mark a real user change: arm preview + tell the chassis (makeChanges tick).
  const markChanged = useCallback(() => {
    userArmed.current = true;
    getShim()?.model.markUserAction();
    ahuBoardBus.emit('rating_mode:mounting:changed');
    bump();
  }, [getShim, bump]);

  // -------------------------------------------------------------------------
  // getLoadings — the DEPENDENCY-FILTERED variant (filter.selector.js:1832-1869)
  // and its listener (js:294-312).
  //
  // The `loadings` RTKQ query above is legacy's getLoadings(TRUE) (js:161): the
  // dependency-FREE call whose result becomes the Loading select's full option
  // list. The port stopped there ("code-only" in the old comment), so the select
  // always offered every loading in the catalogue regardless of the chosen
  // filter/frame. Legacy re-pulls the SAME endpoint with thirteen dependency
  // params on every master/params change, hides the options that did not survive
  // and auto-selects (or blanks) the field.
  //
  // GET-only: the frame_loading_id it may write is a local model.set, and the
  // §49.2 auto-save is suppressed for the dialog's lifetime (shim.rate === true).
  // -------------------------------------------------------------------------
  const buildLoadingsUrl = useCallback((): string => {
    const m = getShim()?.model;
    if (!m) return LOADINGS_URL;
    // js:1837-1838 reads filter_params/frame_params off `self.model` (the BOARD
    // row model) and the master ids off `self.current_model` (the form model).
    // The React port has ONE model behind the shim, carrying both.
    const fp = parseJSON(m.get('filter_params'));
    const frp = parseJSON(m.get('frame_params'));
    const deps: Record<string, unknown> = {
      // js:1841-1855, in legacy's key order (the URL is a cache key).
      filter_id: m.get('master_filter_id'),
      frame_id: m.get('master_frame_id'),
      frame_type_id: frp.frame_type_id,
      mfg_id: frp.mfg_id,
      mtl_id: frp.mtl_id,
      series_id: frp.series_id,
      filter_mfg_id: fp.mfg_id,
      filter_series_id: fp.series_id,
      filter_type_id: fp.type_id,
      filter_merv_id: fp.merv_id,
      filter_efficiency_id: fp.efficiency_id,
      filter_depth_nominal_id: fp.depth_nominal_id,
      filter_ul_class_id: fp.ul_class_id,
    };
    let url = LOADINGS_URL;
    let added = false;
    // js:1856-1861 — falsy values are omitted entirely.
    Object.keys(deps).forEach((key) => {
      if (deps[key]) {
        url += `${added ? '&' : '?'}${key}=${String(deps[key])}`;
        added = true;
      }
    });
    return url;
  }, [getShim]);

  const loadingTimer = useRef<number | undefined>(undefined);
  const refreshLoadings = useCallback(
    async (notUpdate: boolean) => {
      const shim = getShim();
      if (!shim || !shim.rate) return; // js:297-300
      let list: Array<Record<string, unknown>> = [];
      try {
        list = await dispatch(
          filterSelectorApi.endpoints.getFilterLoadings.initiate(
            buildLoadingsUrl(),
            { subscribe: false }
          )
        ).unwrap();
      } catch {
        list = [];
      }
      const m = shim.model;
      const cur = m.get('frame_loading_id');
      // js:1891-1895 — hide everything, unhide the survivors.
      setVisibleLoadingIds(new Set(list.map((el) => String(el.id))));
      // ── changeSelectOptions(js:1888-1917) for frame_loading_id, default
      //    preference order [1, 3, 2] (js:303). `null` = leave the field alone.
      let newVal: unknown = null;
      if ((!notUpdate && list.length !== 1) || list.length < 1) {
        // js:1897-1900 — blank it.
        newVal = '';
      } else {
        const exists =
          cur != null &&
          cur !== '' &&
          list.some((el) => String(el.id) === String(cur));
        // js:1905 — with not_update set, only a single survivor auto-selects.
        if (!exists && (!notUpdate || list.length === 1)) {
          let pick: unknown = [1, 3, 2].find((id) =>
            list.some((el) => String(el.id) === String(id))
          );
          // js:1907
          if (!pick && list.length === 1) pick = list[0].id;
          newVal = pick ?? null; // js:1908-1914
        }
      }
      // js:306-309 — a changed loading re-pulls the bank list.
      if (newVal !== null) {
        m.set({ frame_loading_id: newVal });
        await reloadSizes();
      }
      bump();
    },
    [getShim, dispatch, buildLoadingsUrl, reloadSizes, bump]
  );

  // js:294-312 — change:master_filter_id / master_frame_id / frame_params /
  // filter_params, debounced 300 ms. `not_update` is true when the change came
  // from the FRAME side (js:305).
  useEffect(() => {
    const shim = getShim();
    if (!shim) return undefined;
    const off = shim.model.onChange((changed) => {
      const has = (k: string) =>
        Object.prototype.hasOwnProperty.call(changed, k);
      if (
        !has('master_filter_id') &&
        !has('master_frame_id') &&
        !has('frame_params') &&
        !has('filter_params')
      )
        return;
      const notUpdate = has('frame_params') || has('master_frame_id');
      window.clearTimeout(loadingTimer.current);
      loadingTimer.current = window.setTimeout(() => {
        void refreshLoadings(notUpdate);
      }, 300);
    });
    return () => {
      off();
      window.clearTimeout(loadingTimer.current);
    };
  }, [getShim, refreshLoadings]);

  // -------------------------------------------------------------------------
  // getFrameParamas (filter.selector.js:1919-1951) — the same narrowing for the
  // four Frame selects, from cms/.../filters/frames/{mfgs,series,frame_types,
  // mtls}/ with the filter dependencies. React rendered the full static
  // `collections.frame.*` lists always, so the Frame tab offered manufacturers /
  // series / types / materials that no frame with the chosen filter can have.
  // Wired to change:frame_loading_id|master_filter_id|filter_params (js:314),
  // to render (js:872) and to the has_frame tick (js:679-681).
  // -------------------------------------------------------------------------
  /**
   * The `.change()` half of changeSelectOptions' auto-select (js:1910) — the
   * frame select's own handler, i.e. what onParamsField wraps. Deliberately does
   * NOT call markChanged: getFrameParamas also runs on OPEN (js:872), and
   * opening must leave the userArmed rate gate shut (file header, §4).
   * resetFilter itself is count GETs only.
   */
  const applyFrameParam = useCallback(
    (name: string, raw: string) => {
      const shim = getShim();
      if (!shim) return;
      const field = shim.catalogueFields.master_frame_id;
      if (!field) return;
      const current = field.cmsPages.results_filter.toJSON();
      field.resetFilter({ ...current, [name]: raw === '' ? undefined : raw });
      bump();
    },
    [getShim, bump]
  );

  /**
   * autoSelectFrame (filter.selector.js:358-386) — the last thing legacy does
   * on every getFrameParamas pass (js:1948) and on every `change:frame_params`
   * (js:344).
   *
   * Once Manufacturer + Series + Type are chosen and Material is not, legacy
   * force-picks the frame catalogue's FIRST candidate —
   * `checkIfThereIsOnlyOneFoundModel(TRUE)` ignores the count, so it fires with
   * three candidates left just as it does with one — and the picked row's
   * params (Material included) are written back onto frame_params. That is what
   * takes the Frame tab from three-of-four filled to complete, drops
   * `#available_frame_qty` to 1 and, through checkSelectButtonAvabialty's
   * `avaibleFrames === 1` clause, is what ungreys SELECT and reveals
   * "Updated... Select to Confirm.".
   *
   * The port left this half out (the engine method was not on the shim), so
   * dev3 sat at "5 / 9" with a red Frame tab and a dead SELECT after a filter
   * pick that dev completes on its own. Verified against dev3's catalogue: with
   * the reporter's AAF Flanders / Cartridge SH / Varicel-RF / MERV 11 / 65% /
   * 12" filter and frame Slide + Mfg + Slide-Rack, page 1 of
   * `filters/frames/` is frame 100001, mtl_id 274
   * "Steel-GHD-G-HR-Solid-16ga-0.0635" — the exact Material dev fills in.
   *
   * Legacy's `.then()` half re-homes the SINGLETON smart list between
   * `#catalogue-rows-frame-container` and `#catalogue-rows-filter-container`;
   * this dialog renders a container per side and `changeInputTab` already
   * swaps them, so there is nothing to re-home here.
   *
   * Self-terminating exactly as legacy is: the pick writes `mtl_id`, so the
   * re-entry through `change:frame_params` fails the `!mtl_id` guard.
   */
  const autoSelectFrame = useCallback(() => {
    const shim = getShim();
    if (!shim) return;
    // js:360 has_frame, js:361 rate — an unticked Include Frame has no frame to
    // pick, and a non-rating mount must never auto-write a master id.
    if (!shim.model.get('has_frame') || !shim.rate) return;
    const frp = parseJSON(shim.model.get('frame_params'));
    if (!frp.mfg_id || !frp.series_id || !frp.frame_type_id || frp.mtl_id) return;
    void shim.catalogueFields.master_frame_id
      ?.checkIfThereIsOnlyOneFoundModel(true)
      .then(() => bump());
  }, [getShim, bump]);

  const frameTimer = useRef<number | undefined>(undefined);
  const getFrameParams = useCallback(() => {
    // js:1921-1922 — debounced 300 ms, latest call wins.
    window.clearTimeout(frameTimer.current);
    frameTimer.current = window.setTimeout(() => {
      void (async () => {
        const shim = getShim();
        if (!shim || !shim.rate) return; // js:1923-1926
        const m = shim.model;
        const fp = parseJSON(m.get('filter_params'));
        const frp = parseJSON(m.get('frame_params'));
        const deps: Record<string, unknown> = {
          // js:1935-1943
          filter_id: m.get('master_filter_id'),
          frame_loading_id: m.get('frame_loading_id'),
          filter_mfg_id: fp.mfg_id,
          filter_type_id: fp.type_id,
          filter_efficiency_id: fp.efficiency_id,
          filter_depth_nominal_id: fp.depth_nominal_id,
          filter_ul_class_id: fp.ul_class_id,
        };
        // js:1873-1879 — loadParams' query builder.
        let qs = '';
        let added = false;
        Object.keys(deps).forEach((key) => {
          if (deps[key]) {
            qs += `${added ? '&' : '?'}${key}=${String(deps[key])}`;
            added = true;
          }
        });
        const visible: Record<string, ReadonlySet<string>> = {};
        for (const key of Object.keys(FRAME_FIELDS) as Array<
          keyof typeof FRAME_FIELDS
        >) {
          const endpoint = FRAME_FIELDS[key];
          let list: Array<Record<string, unknown>> = [];
          try {
            const body = await dispatch(
              filterSelectorApi.endpoints.getFilterFrameParams.initiate(
                `${FRAME_PARAMS_BASE}${endpoint}/${qs}`,
                { subscribe: false }
              )
            ).unwrap();
            // js:1885 `list[name] || list` — the viewset answers
            // {"<endpoint>": [...]} (filters_viewset.py:826-853).
            const inner =
              (body as Record<string, unknown> | null | undefined)?.[endpoint] ??
              body;
            list = Array.isArray(inner)
              ? (inner as Array<Record<string, unknown>>)
              : [];
          } catch {
            list = [];
          }
          visible[key] = new Set(list.map((el) => String(el.id)));
          // ── changeSelectOptions(list, key, frame_params[key], [], true, true)
          //    — js:1946. params_frame ⇒ the auto-select COMMITS (`.change()`).
          const cur = frp[key];
          const chosen = cur != null && cur !== '';
          if (!list.length) {
            // js:1901-1904 — nothing survives: blank the select.
            if (chosen) applyFrameParam(key, '');
          } else {
            const exists = list.some((el) => String(el.id) === String(cur));
            // js:1905-1907 — not_update is true and default_ids empty, so the
            // ONLY auto-select is "exactly one option left".
            if (!exists && list.length === 1)
              applyFrameParam(key, String(list[0].id));
          }
        }
        setVisibleFrameParams(visible);
        autoSelectFrame(); // js:1948
      })();
    }, 300);
  }, [getShim, dispatch, applyFrameParam, autoSelectFrame]);

  // js:314 — change:frame_loading_id / master_filter_id / filter_params.
  useEffect(() => {
    const shim = getShim();
    if (!shim) return undefined;
    const off = shim.model.onChange((changed) => {
      const has = (k: string) =>
        Object.prototype.hasOwnProperty.call(changed, k);
      if (
        has('frame_loading_id') ||
        has('master_filter_id') ||
        has('filter_params')
      )
        getFrameParams();
      // js:344 — `listenTo(current_model, "change:frame_params",
      // autoSelectFrame)`. Separate from the getFrameParamas trigger above:
      // picking Manufacturer / Series / Type on the Frame tab rewrites
      // frame_params WITHOUT touching filter_params, so this is the edit that
      // completes the trio and has to be able to fire the pick on its own.
      if (has('frame_params')) autoSelectFrame();
    });
    return () => {
      off();
      window.clearTimeout(frameTimer.current);
    };
  }, [getShim, getFrameParams, autoSelectFrame]);

  // js:872 — render() kicks one pass so the selects are narrowed on open.
  useEffect(() => {
    getFrameParams();
  }, [getFrameParams]);

  // -------------------------------------------------------------------------
  // changeDepth (filter.selector.js:1953-1962) — bound at js:49 to the Depth
  // select, and also called from the select:model handler (js:340) and from
  // render's 500 ms settle (js:892).
  //
  // A filter deeper than 4 inches — OR arrangement 2 — cannot be angled: legacy
  // ZEROES mounting_angle and makes the input non-clickable. React only greyed
  // the input out for arrangement 2 (MountingPanel) and never zeroed the value,
  // so a 12 in filter could keep a 45° angle and go on rating with it.
  //
  // The depth read is the SELECTED FILTER's nominal depth: filter_params
  // .depth_nominal_id resolved against collections.nominal_depth's `value`
  // (the numeric inches), NOT its `name` (js:1957).
  // -------------------------------------------------------------------------
  const applyDepthLock = useCallback(
    (depthIdOverride?: string, fromUser = false) => {
      const shim = getShim();
      if (!shim) return;
      const m = shim.model;
      const fp = parseJSON(m.get('filter_params'));
      // js:1956 — the event's own value wins over the stored one.
      const depthId = depthIdOverride || fp.depth_nominal_id;
      const row = (collections.nominal_depth ?? []).find(
        (r) => String(r.id) === String(depthId ?? '')
      );
      const depth = num(row?.value);
      // js:1959 — `!!(depth && Number(depth) > 4) || arr == 2`.
      const lock =
        (depth != null && depth > 4) ||
        Number(m.get('arrangement_id')) === 2;
      // js:1960 `input.val(0).change()` — the .change() re-enters
      // changeModelField, which re-rates and re-pulls the bank list. On OPEN
      // that would be a PATCH before any user gesture, so the open-time call
      // only sets the value locally; a user-driven call takes the full path.
      if (lock && Number(m.get('mounting_angle')) !== 0) {
        if (fromUser) {
          markChanged();
          m.set({ mounting_angle: 0 });
          void getFilterData().then(() => reloadSizes());
        } else {
          m.set({ mounting_angle: 0 }, { silent: true });
        }
      }
      setMountingAngleLocked(lock); // js:1961
    },
    [getShim, collections, markChanged, getFilterData, reloadSizes]
  );

  // js:892 — render's settle calls changeDepth once, so an already-rated deep
  // filter opens with Mounting Angle locked.
  useEffect(() => {
    applyDepthLock();
  }, [applyDepthLock]);

  // -------------------------------------------------------------------------
  // bank-configuration click (legacy changeBankConfiguration)
  // -------------------------------------------------------------------------
  const arrowTimer = useRef<number | undefined>(undefined);
  const widthNominal = useRef<number | null>(null);
  const heightNominal = useRef<number | null>(null);

  const selectBankConfig = useCallback(
    async (rowId: number, w: number, h: number, fromUser: boolean) => {
      const shim = getShim();
      if (!shim) return;
      const m = shim.model;
      const prevSizeQty = m.get('size_qty_id');
      widthNominal.current = w || null;
      heightNominal.current = h || null;
      if (fromUser) {
        markChanged();
        m.set({ size_qty_id: rowId });
        if (prevSizeQty != rowId || !availableWidthHeight.current) {
          await getFilterData();
        }
      } else {
        // open-time / programmatic selection — set silently, no rate PATCH.
        m.set({ size_qty_id: rowId }, { silent: true });
      }
      bump();
      drawBankPlacement();
    },
    [getShim, markChanged, getFilterData, bump]
  );

  // keyboard nav on the bank list (legacy bankConfigKeydown — arrow up/down).
  const onBankKeydown = useCallback(
    (e: React.KeyboardEvent<HTMLUListElement>) => {
      if (e.keyCode !== 38 && e.keyCode !== 40) return;
      e.preventDefault();
      e.stopPropagation();
      const shim = getShim();
      if (!shim) return;
      const curId = shim.model.get('size_qty_id');
      // legacy reads data-index off the selected <li>, and the pinned row's is
      // -1 (tmpl:43) — so Down from the pin steps onto the first real row.
      // Bailing on "not found" left the arrows dead whenever a row was pinned.
      const idx = bankRows.findIndex((r) => r.id == curId);
      const nextIdx = e.keyCode === 38 ? idx - 1 : idx + 1;
      const next = bankRows[nextIdx];
      if (next) {
        // legacy arrow_down_up_timer (js:1789-1823): the row highlights at
        // once but the apply is debounced 300ms, so holding an arrow key
        // fires ONE rate round-trip, not one per keypress.
        window.clearTimeout(arrowTimer.current);
        arrowTimer.current = window.setTimeout(() => {
          void selectBankConfig(next.id, next.bank_width, next.bank_height, true);
        }, 300);
        const li = bankUlRef.current?.querySelector<HTMLLIElement>(
          `li[data-id="${next.id}"]`
        );
        li?.scrollIntoView({ behavior: 'smooth', block: e.keyCode === 38 ? 'end' : 'start' });
      }
    },
    [bankRows, getShim, selectBankConfig]
  );

  // -------------------------------------------------------------------------
  // drawBankPlacement — the bank grid (legacy DOM math, kept imperative).
  // -------------------------------------------------------------------------
  const drawBankPlacement = useCallback(() => {
    const shim = getShim();
    if (!shim) return;
    const selected = bankUlRef.current?.querySelector<HTMLLIElement>(
      'li.selected'
    );
    const selectedConfig = selected?.getAttribute('data-size');
    const byHeight = document.getElementById('filter_by_height');
    if (!byHeight) return;
    // legacy js:1111 clears BEFORE it uses the selection, but its guard at
    // :1108 returns first — so on a reload that leaves nothing selected the old
    // drawing stayed. We clear first, which is what the reload path (js:1668)
    // does explicitly, so a stale bank never lingers.
    byHeight.innerHTML = '';
    if (!selectedConfig) return;
    const cfg = parseDimensions(selectedConfig);
    cfg.H.forEach((high) => {
      for (let i = 0; i < high.count; i += 1) {
        const rowDiv = document.createElement('div');
        rowDiv.className = `filter-by-width h-${high.value}`;
        rowDiv.id = `h_${high.value}_${i}`;
        cfg.W.forEach((wide) => {
          for (let j = 0; j < wide.count; j += 1) {
            const cell = document.createElement('div');
            cell.className = `single-filter w-${wide.value}`;
            rowDiv.appendChild(cell);
          }
        });
        byHeight.prepend(rowDiv);
      }
    });
    const sw = sectionDims().w;
    const sh = sectionDims().h;
    const drawEl = document.querySelector<HTMLElement>('.bank-configuration-draw');
    const airflowArea = document.querySelector<HTMLElement>('.airflow-area');
    if (!drawEl || !airflowArea) return;
    const bankWidth = drawEl.offsetWidth;
    let drawCoeff: number;
    let areaW: number;
    let areaH: number;
    if (sh > sw) {
      areaH = bankWidth - 30;
      const coeff = areaH / sh;
      areaW = coeff * sw;
      drawCoeff = areaH / sh;
    } else {
      areaW = bankWidth - 30;
      const coeff = areaW / sw;
      areaH = coeff * sh;
      drawCoeff = areaW / sw;
    }
    airflowArea.style.width = `${areaW}px`;
    airflowArea.style.height = `${areaH}px`;
    ['12', '16', '20', '24'].forEach((size) => {
      document.querySelectorAll<HTMLElement>(`.w-${size}`).forEach((el) => {
        el.style.width = `${drawCoeff * Number(size)}px`;
      });
      document.querySelectorAll<HTMLElement>(`.h-${size}`).forEach((el) => {
        el.style.height = `${drawCoeff * Number(size)}px`;
      });
    });
    const height = Number(shim.model.get('height')) || 0;
    const scaleY = heightNominal.current ? height / heightNominal.current : 1;
    const hFromFloor = Number(shim.model.get('height_from_floor')) || 0;
    const byHeightEl = document.querySelector<HTMLElement>('.filter-by-height');
    if (byHeightEl) {
      byHeightEl.style.transform = `scaleY(${scaleY || 1})`;
      byHeightEl.style.paddingBottom = `${(hFromFloor * drawCoeff) / (scaleY || 1)}px`;
      byHeightEl.style.paddingLeft = '0px';
      if (areaW - byHeightEl.clientWidth > 0) {
        byHeightEl.style.paddingLeft = `${(areaW - byHeightEl.clientWidth) / 2}px`;
      }

      // ── overflow warning (js:1188-1249) — not ported, so a bank that does
      // not fit the airway drew normally with no indication. Cells that spill
      // past .airflow-area get a red border.
      const parent = airflowArea;
      const parentRect = parent.getBoundingClientRect();
      const firstRow = document.querySelector<HTMLElement>('.filter-by-width');
      if (firstRow && areaW < firstRow.offsetWidth) {
        parent.querySelectorAll<HTMLElement>('.filter-by-width').forEach((child) => {
          const cr = child.getBoundingClientRect();
          if (
            cr.bottom <= parentRect.bottom &&
            cr.left >= parentRect.left &&
            cr.right >= parentRect.right
          ) {
            child
              .querySelectorAll<HTMLElement>('.w-24, .w-20, .w-16, .w-12')
              .forEach((item) => {
                const ir = item.getBoundingClientRect();
                if (
                  ir.bottom <= parentRect.bottom &&
                  ir.left >= parentRect.left &&
                  ir.right >= parentRect.right
                ) {
                  item.style.borderColor = 'red';
                }
              });
          }
        });
      }
      if (areaH < byHeightEl.offsetHeight) {
        parent.querySelectorAll<HTMLElement>('.filter-by-height').forEach((child) => {
          const cr = child.getBoundingClientRect();
          if (
            cr.top <= parentRect.top &&
            cr.bottom <= parentRect.bottom &&
            cr.left >= parentRect.left
          ) {
            child
              .querySelectorAll<HTMLElement>('.h-24, .h-20, .h-16, .h-12')
              .forEach((item) => {
                const ir = item.getBoundingClientRect();
                if (
                  ir.top <= parentRect.top &&
                  ir.bottom <= parentRect.bottom &&
                  ir.left >= parentRect.left
                ) {
                  item
                    .querySelectorAll<HTMLElement>('.single-filter')
                    .forEach((last) => {
                      last.style.borderColor = 'red';
                    });
                }
              });
          }
        });
      }
    }
  }, [getShim]);

  const sectionDims = useCallback((): { w: number; h: number } => {
    const fromActivate = {
      w: num(activate.section?.aw_width),
      h: num(activate.section?.aw_height),
    };
    if (fromActivate.w != null && fromActivate.h != null)
      return { w: fromActivate.w, h: fromActivate.h };
    const shim = getShim();
    const dims = shim?.getSectionDims?.();
    return { w: Number(dims?.aw_width) || 0, h: Number(dims?.aw_height) || 0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activate.section, getShim]);

  /**
   * legacy render() (js:880-894) waits 500ms then CLICKS the selected
   * `<li>` (or the first row when the saved one is gone), which routes through
   * changeBankConfiguration and sets `width_nominal_value` /
   * `height_nominal_value` from the row's data-width / data-height (js:565-570).
   *
   * The port never did this, so on open widthNominal/heightNominal stayed null
   * and the output pane's Bank W x H, Effective W x H and Face Area were all
   * blank — even for a section that already HAS a filter — and
   * drawBankPlacement fell back to scaleY = 1.
   *
   * We seed the two refs directly instead of simulating the click: the click
   * path also fires the getFilterData rate PATCH, and opening this dialog must
   * stay write-free (see the DATA-SAFETY note in the file header). Everything
   * the click derived CLIENT-side is recovered here; only
   * `avaliable_width_height`, which is server-computed, still needs the
   * round-trip and so still waits for a real user change.
   */
  const seedNominalsFromSelection = useCallback(
    (list: IFilterSizeQtyRow[], pinned?: IFilterSizeQtyRow | null) => {
      const shim = getShim();
      if (!shim) return;
      const savedId = shim.model.get('size_qty_id');
      const row =
        (pinned && pinned.id == savedId ? pinned : undefined) ??
        list.find((r) => r.id == savedId) ??
        pinned ??
        undefined;
      if (!row) return;
      widthNominal.current = row.bank_width || null;
      heightNominal.current = row.bank_height || null;
      bump();
      drawBankPlacement();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getShim, bump]
  );

  // re-draw bank placement whenever bank rows / selection changes.
  useEffect(() => {
    drawBankPlacement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankRows]);

  // Tail of legacy getFilterSizesAndQuantities (js:1692-1695): after the list is
  // replaced, scroll the saved configuration's row into view — but only when it
  // is not already visible INSIDE the list's own scroll box
  // (`isElementInViewport`, js:1779-1787, with its 6 px slack). An unconditional
  // scrollIntoView would jump the whole dialog for a row already on screen.
  // Armed by the sizer re-seed only; the other paths reach here through a click
  // or a keypress, which scroll themselves.
  useEffect(() => {
    if (!scrollToSelectedBank.current) return;
    scrollToSelectedBank.current = false;
    const ul = bankUlRef.current;
    const shim = getShim();
    const savedId = shim?.model.get('size_qty_id');
    if (!ul || savedId == null) return;
    const li = ul.querySelector<HTMLLIElement>(`li[data-id="${String(savedId)}"]`);
    if (!li) return;
    const liRect = li.getBoundingClientRect();
    const ulRect = ul.getBoundingClientRect();
    const visible =
      liRect.top - 6 >= ulRect.top && liRect.bottom - 6 <= ulRect.bottom;
    if (!visible) li.scrollIntoView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankRows, bankById]);

  // -------------------------------------------------------------------------
  // model field edits (legacy changeModelField — selected subset triggers
  // getFilterData; all arm userArmed)
  // -------------------------------------------------------------------------
  // Dev-sync J6 — changeFilterSectionField (filter.selector.js:692-734):
  // number inputs clamp to data-min/max + 2 decimals; stage on sectionDraft
  // ONLY (no persist until Select). A season change re-renders so the gated
  // groups swap (renderPerformanceTab); React's bump covers both that and the
  // output-mirror refresh (render_output). Deliberately NOT reusing the
  // model-field path — these are section-only names (the legacy comment's
  // bogus-model-attribute concern).
  const onFilterSectionField = useCallback(
    (name: string, rawValue: string, minV?: number, maxV?: number): number | null => {
      let value: number | null;
      if (rawValue === '' || rawValue === 'None') {
        value = null;
      } else {
        let n = Number(String(rawValue).replace(/,/g, ''));
        if (Number.isNaN(n)) n = 0;
        n = Math.min(Math.max(n, minV ?? -Infinity), maxV ?? Infinity);
        value = Number(n.toFixed(2));
      }
      sectionDraft.current = { ...sectionDraft.current, [name]: value };
      bump();
      return value;
    },
    [bump]
  );

  const onModelField = useCallback(
    async (name: string, rawValue: string | boolean) => {
      const shim = getShim();
      if (!shim) return;
      const m = shim.model;
      const value: unknown =
        typeof rawValue === 'boolean' ? rawValue : rawValue;
      if (m.get(name) == value || (!value && !m.get(name))) return;
      // ── distance_from_section_ups floor (js:600-609) ─────────────────────
      // The CMS frame clearance (clear_min_ups_in_section, on the filter
      // serializer — filter_serializers.py:210) is a hard minimum: legacy
      // rewrites the input, sets the floor on the model and RETURNS, skipping
      // the rate round-trip and the bank re-pull for the rejected value.
      // NB this runs BEFORE markChanged(): legacy returns long before it
      // reaches the `.filter_has_changes.addClass('active')` at the end of
      // changeModelField, so a rejected value must not arm userArmed (which
      // permanently opens the rate-PATCH gate) or raise "Updated…".
      if (name === 'distance_from_section_ups') {
        const floor = num(m.get('clear_min_ups_in_section')) ?? 0;
        const entered = num(value);
        if (entered != null && entered < floor) {
          m.set({ distance_from_section_ups: floor });
          repaintNumber('distance_from_section_ups'); // js:604 `.val(floor)`
          bump();
          return;
        }
      }
      markChanged();
      if (name === 'final_recommended_apd') finalApdChanged.current = true;
      m.set({ [name]: value });
      // ── Face Velocity min/max coupling (js:613-625) ──────────────────────
      // The data-min / data-max cross-writes are already carried by the markup
      // bindings; this is the CLAMP half — raising min above max drags max up
      // with it, and vice versa. (Legacy's `+get(...)` makes a null partner 0,
      // so a blank max is dragged up too; Number() reproduces that exactly.)
      if (name === 'min_face_velocity') {
        if (Number(m.get('max_face_velocity')) < Number(value))
          m.set({ max_face_velocity: value });
      } else if (name === 'max_face_velocity') {
        if (Number(m.get('min_face_velocity')) > Number(value))
          m.set({ min_face_velocity: value });
      }
      // ── arrangement_id zeroes mounting_angle (js:626-629) ────────────────
      // An angled bank is meaningless for arrangement 2, so legacy locks the
      // input AND zeroes it. Note legacy re-derives the lock from `value == 2`
      // ALONE here — a depth > 4 lock set by changeDepth is dropped until the
      // next depth change (js:627 vs js:1961); reproduced as-is.
      if (name === 'arrangement_id') {
        setMountingAngleLocked(String(value) === '2');
        // js:628 fires `.val(0).change()`, i.e. a SECOND changeModelField that
        // re-rates on its own. We set it before this call's own getFilterData
        // below, so the one round-trip already carries mounting_angle = 0.
        if (Number(m.get('mounting_angle')) !== 0) m.set({ mounting_angle: 0 });
      }
      // legacy: these fields trigger a rate round-trip.
      if (
        name === 'arrangement_id' ||
        name === 'mounting_angle' ||
        name === 'airflow_actual' ||
        name === 'final_recommended_apd' ||
        name === 'center_high' ||
        name === 'height_from_floor' ||
        name === 'frame_loading_id'
      ) {
        await getFilterData();
      } else if (name === 'air_stream_id') {
        applyAirStreamAirflow(String(rawValue));
      } else if (name === 'has_frame') {
        checkSelectAvailability();
        // js:679-681 — ticking Include Frame re-narrows the Frame selects.
        if (value) getFrameParams();
      } else if (name === 'max_different_sizes') {
        // ── max_different_sizes clamp (js:657-665) ─────────────────────────
        // Legacy pins it to [1, min(allowable_filter_sizes.length, 3)] and
        // writes the clamped number back into the input; React wrote the raw
        // value, so "9" survived into the bank query and returned nothing.
        sizesPage.current = 1; // js:658
        const allowableCount = allowableSizes.current.length;
        if (Number(value) > allowableCount) {
          m.set({ max_different_sizes: Math.min(allowableCount, 3) });
          repaintNumber('max_different_sizes'); // js:661 `$(e.target).val(...)`
        } else if (Number(value) < 1) {
          m.set({ max_different_sizes: 1 });
          repaintNumber('max_different_sizes'); // js:664
        }
      }
      // legacy: these re-pull the bank list (userArmed already armed).
      if (
        [
          'arrangement_id',
          'center_high',
          'mounting_angle',
          'has_frame',
          'frame_loading_id',
          'height_from_floor',
          'selection_condition',
          'airflow_actual',
          'max_different_sizes',
          'min_face_velocity',
          'max_face_velocity',
        ].includes(name)
      ) {
        await reloadSizes();
      }
      bump();
    },
    [
      getShim,
      markChanged,
      getFilterData,
      reloadSizes,
      getFrameParams,
      repaintNumber,
      bump,
    ]
  );

  // air_stream_id → default airflow off the AHU volumes (legacy getAHUAirflow).
  const applyAirStreamAirflow = useCallback(
    (streamId: string) => {
      const shim = getShim();
      const ahu = activate.ahu as Record<string, unknown> | undefined;
      if (!shim || !ahu) return;
      const map: Record<string, string> = {
        '1': 'sa_volume_max',
        '2': 'ra_volume_max',
        '3': 'oa_volume_max',
        '4': 'ea_volume_max',
      };
      const key = map[streamId];
      const def = key ? num(ahu[key]) : null;
      if (def != null && num(shim.model.get('airflow_actual')) !== def) {
        shim.model.set({ airflow_actual: def });
        void getFilterData();
      }
    },
    [getShim, activate.ahu, getFilterData]
  );

  // -------------------------------------------------------------------------
  // catalogue param edit (mfg/type/series/merv/efficiency/depth/ul_class) →
  // the form's CatalogueFieldEngine.updateFilter (its counts + auto-pick).
  // -------------------------------------------------------------------------
  const onParamsField = useCallback(
    (target: 'master_filter_id' | 'master_frame_id', name: string, raw: string) => {
      const shim = getShim();
      if (!shim) return;
      markChanged();
      const engine = (
        shim.catalogueFields[target] as unknown as {
          resetFilter?: (a: Record<string, unknown>) => void;
        }
      );
      // The shim exposes resetFilter (#6); the live engine's updateFilter is
      // reached through the same field. We write the single param via resetFilter
      // merge (mirrors the form's params_filter select → updateFilter).
      const current = shim.catalogueFields[target].cmsPages.results_filter.toJSON();
      engine.resetFilter?.({ ...current, [name]: raw === 'None' || raw === '' ? undefined : raw });
      // js:49 — `change .params_filter[name="depth_nominal_id"]` is a SEPARATE
      // binding that runs alongside the params handler.
      if (target === 'master_filter_id' && name === 'depth_nominal_id')
        applyDepthLock(raw === 'None' ? '' : raw, true);
      bump();
    },
    [getShim, markChanged, applyDepthLock, bump]
  );

  // -------------------------------------------------------------------------
  // allowable-sizes multiselect (legacy toggleMultiSelect / changeAllowable /
  // applySizes / hideAllowableFilterSizes)
  // -------------------------------------------------------------------------
  const toggleAllowable = useCallback((sizeId: number | '', checked: boolean) => {
    if (!changedAllowable.current.length)
      changedAllowable.current = [...allowableSizes.current];
    if (sizeId === '') {
      changedAllowable.current = checked ? [...filterSizesRef.current] : [];
    } else if (checked) {
      const hit = filterSizesRef.current.find((el) => el.id == sizeId);
      if (hit) changedAllowable.current.push(hit);
    } else {
      changedAllowable.current = changedAllowable.current.filter(
        (el) => el.id != sizeId
      );
    }
    bump();
  }, [bump]);

  const filterSizesRef = useRef<IFilterSizeRow[]>([]);
  filterSizesRef.current = filterSizes;

  const applyAllowableSizes = useCallback(async () => {
    const shim = getShim();
    allowableSizes.current = [...changedAllowable.current];
    if (
      shim &&
      allowableSizes.current.length < Number(shim.model.get('max_different_sizes'))
    ) {
      markChanged();
      shim.model.set({ max_different_sizes: allowableSizes.current.length });
    }
    setSizesPopupOpen(false);
    markChanged();
    await reloadSizes();
    bump();
  }, [getShim, markChanged, reloadSizes, bump]);

  // hide:show_allowable_filter_sizes listener (legacy hideAllowableFilterSizes).
  useEffect(() => {
    const off = ahuBoardBus.on('hide:show_allowable_filter_sizes', () => {
      changedAllowable.current = [...allowableSizes.current];
      setSizesPopupOpen(false);
      bump();
    });
    return off;
  }, [bump]);

  // -------------------------------------------------------------------------
  // smart-list toggle (btn-sync) — relocated rows render React-side
  // -------------------------------------------------------------------------
  /**
   * The 50ms `animate-rotate` window both directions share
   * (catalogue_selector.js:234-239 open, :255-261 close).
   *
   * `.animate-rotate` is `transform: rotateX(-180deg)` (common.css:24836) and
   * both `#filter_smart_list` / `#frame_smart_list` (:23887) and
   * `#bank-configuration-draw-block` (:24221) carry
   * `transform-style: preserve-3d; transition: transform 1s`. Legacy puts the
   * class on BOTH nodes at t=0 and takes it off BOTH at t=50 — and inside that
   * window the ARRIVING node is un-hidden, so it paints once already flipped
   * and then transitions the 180° back over a second, while the LEAVING node
   * is simply `hide`-ed at t=50 (its own flip never gets to run). That is the
   * whole animation: one element flips in, the other blinks out 50ms in.
   */
  const runRotate = useCallback((atEnd: () => void) => {
    window.clearTimeout(rotateTimer.current);
    setRotating(true);
    rotateTimer.current = window.setTimeout(() => {
      rotateTimer.current = undefined;
      setRotating(false);
      atEnd();
    }, 50);
  }, []);
  useEffect(() => () => window.clearTimeout(rotateTimer.current), []);

  /**
   * `rotateBack` in `with_rating` mode (catalogue_selector.js:249-263): the
   * sibling loses `hide` immediately, the list keeps it until t=50, and the
   * dispatcher fires at the end of the window.
   */
  const closeSmartList = useCallback(() => {
    runRotate(() => {
      setSmartList(null);
      ahuBoardBus.emit('smart_list_rotate_back');
    });
  }, [runRotate]);

  /**
   * `activateInputLabel` (filter.selector.js:1277-1292) does more than swap the
   * pane: while `#bank-configuration-draw-block` carries `hide` — i.e. the smart
   * list is rotated open — it clicks the *other* pane's Smartlist button and
   * blanks `catalogueRowsContainer.innerHTML`, so the list rotates shut and the
   * block comes back. Here the list is per-pane state, so the tab change closed
   * nothing: `smartList` stayed set, the block kept `hide` (see its className),
   * and "Bank Configuration Wide x High" was gone for the rest of the dialog.
   */
  const changeInputTab = useCallback(
    (t: InputTab) => {
      setInputTab(t);
      if (!smartList) return;
      closeSmartList();
      // legacy blanks the rows container in the same breath, so re-opening the
      // list refetches from page 1 behind `#list-preloader` rather than showing
      // the previous tab's rows.
      setSmartRows([]);
      smartRowsSide.current = null;
    },
    [smartList, closeSmartList]
  );

  /**
   * One page-1 request for `side`, with the field's CURRENT filters — legacy's
   * `load()` (catalogue_selector.js:135-145). Shared by the Smartlist button and
   * by Reset, which re-runs it in place (`resetCatalogue`, :1133-1147).
   */
  const loadSmartPage = useCallback(
    async (side: 'filter' | 'frame') => {
      const shim = getShim();
      if (!shim) return;
      const field =
        side === 'filter'
          ? shim.catalogueFields.master_filter_id
          : shim.catalogueFields.master_frame_id;
      // EVERY results_filter, not just index 0. Legacy's `load_page`
      // (cms.components.pages.js:317-330) hands the whole `results_filters`
      // array to the catalogue collection, exactly as `fetchCount` hands it to
      // the counter — so the rows the list offers are the rows the "n / N"
      // badge counted. Building this from `results_filter` alone dropped the
      // compatibility filter (index 1: filter_id / frame_loading_id /
      // filter_* on the frame side, arrangement_id / bank_configuration_id /
      // frame on the filter side), so the list offered frames that do not pair
      // with the selected filter; picking one left the count at 0 — blank
      // badge, red Frame tab, Select greyed.
      //
      // Read at call time, never closed over: Reset clears filters[0]
      // synchronously (catalogueField.ts:390) and then asks for this page, so
      // the query has to be built from the field as it is NOW.
      const filters = field.filtersQuery();
      const sourceURL =
        side === 'filter'
          ? '/api/v1.0/cms/1/v1.0.0/ahu/filters/all/'
          : '/api/v1.0/cms/1/v1.0.0/ahu/filters/frames/all/';
      // NB: legacy skips the request entirely when the count is 0 —
      // `checkScroll` gates every `load()` on `has_available_pages()`
      // (`cms_count && loaded_count < cms_count`, cms.components.pages.js:152-155).
      // Not copied deliberately: the shim's `cms_count` getter collapses "0" and
      // "count not fetched yet" into the same 0 (catalogueField.ts:584), so
      // skipping on it would strand a real list empty. Fetching always and
      // clearing the flag on settle reaches the same end state — empty list, no
      // spinner — one round trip later.
      const seq = ++smartFetchSeq.current;
      setSmartLoading(true);
      try {
        const rows = await dispatch(
          ahuComponentFormsApi.endpoints.getCatalogPage.initiate(
            { sourceURL, page: 1, filters },
            { subscribe: false }
          )
        ).unwrap();
        // a newer request (side switch, or a second Reset) owns the list now.
        if (smartFetchSeq.current !== seq) return;
        smartRowsSide.current = side;
        setSmartRows(rows);
      } catch {
        if (smartFetchSeq.current !== seq) return;
        smartRowsSide.current = side;
        setSmartRows([]);
      } finally {
        if (smartFetchSeq.current === seq) setSmartLoading(false);
      }
    },
    [getShim, dispatch]
  );

  const toggleSmartList = useCallback(
    async (side: 'filter' | 'frame') => {
      const shim = getShim();
      if (!shim) return;
      if (smartList === side) {
        closeSmartList();
        return;
      }
      // `rotateOpen` is synchronous: the list is on screen (flipping in) before
      // the first page is even requested, and `load()` paints `#list-preloader`
      // inside it while that request is in flight (catalogue_selector.js:138).
      // Awaiting the fetch first held the panel back by a whole round-trip.
      if (smartRowsSide.current !== side) setSmartRows([]);
      setSmartList(side);
      runRotate(() => {});
      await loadSmartPage(side);
    },
    [getShim, smartList, closeSmartList, runRotate, loadSmartPage]
  );

  // catalogue Reset — legacy binds BOTH #filters_reset_btn and #frames_reset_btn
  // to `deselectModel()` (catalogue_select_field.js:608-619), which clears
  // results_filter[0], nulls master_<x>_id and rewrites the params field.
  //
  // This used to call `resetFilter({}, false, 1)` — filter index ONE with empty
  // attrs. That is the SECONDARY filter, which FilterForm.tsx:50-58 populates
  // with {arrangement_id, bank_configuration_id, frame}: so Reset neither
  // deselected the filter nor cleared Manufacturer/Type/Series, and it silently
  // dropped the bank-configuration constraint from every later count and
  // smart-list query for the rest of the dialog session.
  //
  // Legacy's `deselectModel` tail is `resetCatalogue()`
  // (catalogue_select_field.js:850 → :1133-1147), which is where the smart list
  // is kept in step with the filters the Reset just cleared:
  //
  //   await self.cmsPages.reset();
  //   self.catalogueSelector.clear();          // outdated rows go, always
  //   if (self.catalogueSelector.rotated) {    // still OPEN — never rotated back
  //     self.catalogueSelector.owner.selected = false;
  //     self.catalogueSelector.updateCatalogue();   // → clear + load page 1
  //   }
  //
  // The port stopped at `deselectModel()`, so the badge re-counted but the rows
  // on screen stayed whatever the pre-Reset filters had returned.
  const resetCatalogueFor = useCallback(
    (target: 'master_filter_id' | 'master_frame_id') => {
      const shim = getShim();
      if (!shim) return;
      markChanged();
      const field = shim.catalogueFields[target];
      field.deselectModel();
      // `deselectModel` empties results_filter[0] (cms_id survives it — the
      // engine re-pins it, catalogueField.ts), which leaves UL Class showing a
      // value the filter no longer carries. Put the one option on offer back
      // in, so the post-Reset count / page / rate requests ask for the filters
      // the pane is describing. Synchronous, so the `loadSmartPage` below —
      // which reads `filtersQuery()` at call time — sees it.
      if (target === 'master_filter_id' && soleUlClassId != null) {
        const current = field.cmsPages.results_filter.toJSON();
        if (current.ul_class_id == null)
          field.resetFilter({ ...current, ul_class_id: soleUlClassId });
      }
      bump();
      const side = target === 'master_filter_id' ? 'filter' : 'frame';
      // `clear()` runs whether or not the list is open, but ONLY under
      // `isOwner(self)` — the one shared selector's rows have to belong to the
      // field being reset. `smartRowsSide` is that ownership here, so a Frame
      // Reset while the Filter list is on screen leaves the Filter rows alone.
      // (`smartList === side` covers the list being open on a first fetch that
      // has not landed yet, so `smartRowsSide` is not set to it.)
      if (smartRowsSide.current !== side && smartList !== side) return;
      setSmartRows([]);
      smartRowsSide.current = null;
      // rotated open on this field → refill in place, list stays open. The
      // preloader covers the round trip exactly as legacy's `#list-preloader`
      // does (the rows container is empty again, so `load()` paints it), and
      // the seq bump inside discards any pre-Reset fetch still in flight.
      if (smartList === side) void loadSmartPage(side);
    },
    [getShim, markChanged, bump, smartList, loadSmartPage, soleUlClassId]
  );
  const resetCatalogue = useCallback(
    () => resetCatalogueFor('master_filter_id'),
    [resetCatalogueFor]
  );
  const resetFrameCatalogue = useCallback(
    () => resetCatalogueFor('master_frame_id'),
    [resetCatalogueFor]
  );

  // smart-list row pick → the form's selectModelWithID (a user gesture).
  const pickSmartRow = useCallback(
    (row: IMasterRow, side: 'filter' | 'frame') => {
      const shim = getShim();
      if (!shim) return;
      markChanged();
      // route through the form's CatalogueFieldEngine via the choose:* bus wire
      // the engine already subscribes (catalogueField.ts ctor): a user smart-
      // list pick = choose:<componentName>:master {id, component_id}.
      ahuBoardBus.emit(
        `choose:${side === 'filter' ? 'filter' : 'frame'}:master` as `choose:${string}:master`,
        { id: row.id, component_id: modelId }
      );
      // catalogue_select_field.js:683 calls rotateBack() on a row pick, and the
      // `with_rating` branch returns before `onlyHide` is looked at — so a pick
      // closes with the same 50ms flip as the Smartlist button.
      closeSmartList();
    },
    [getShim, markChanged, modelId, closeSmartList]
  );

  // -------------------------------------------------------------------------
  // select:model:for_<id> (the form's engine fired a master pick) → re-rate +
  // re-pull frame filters (legacy listenTo select:model:for_<model_id>).
  // -------------------------------------------------------------------------
  useEffect(() => {
    const key = `select:model:for_${modelId}` as `select:model:for_${number}`;
    const off = ahuBoardBus.on(key, (...args: unknown[]) => {
      const shim = getShim();
      if (!shim) return;
      const model = (args[0] ?? {}) as Record<string, unknown>;
      const filterParams = parseJSON(model.filter_params);
      shim.catalogueFields.master_frame_id.resetFilter(
        {
          filter_id: model.master_filter_id || null,
          frame_loading_id: model.frame_loading_id,
          filter_mfg_id: filterParams.mfg_id,
          filter_type_id: filterParams.type_id,
          filter_efficiency_id: filterParams.efficiency_id,
          filter_depth_nominal_id: filterParams.depth_nominal_id,
          filter_ul_class_id: filterParams.ul_class_id,
          filter_merv_id: filterParams.merv_id,
          filter_series_id: filterParams.series_id,
        },
        false,
        1
      );
      // a smart-list / catalogue pick is a real user change → preview re-rate.
      userArmed.current = true;
      void getFilterData();
      // js:340 — a freshly picked master carries its own nominal depth, so the
      // Mounting Angle lock is re-derived here too.
      applyDepthLock(undefined, true);
      bump();
    });
    return off;
  }, [modelId, getShim, getFilterData, applyDepthLock, bump]);

  // update:filter:<id> (form → selector re-init data injection).
  useEffect(() => {
    const key = `update:filter:${modelId}` as `update:filter:${number}`;
    const off = ahuBoardBus.on(key, (...args: unknown[]) => {
      init.current = (args[0] ?? init.current) as Record<string, unknown>;
    });
    return off;
  }, [modelId]);

  // smart_list_rotate_back (returns from CompTable flip) → re-click selected
  // bank config (legacy listener).
  useEffect(() => {
    let timer: number | undefined;
    const off = ahuBoardBus.on('smart_list_rotate_back', () => {
      timer = window.setTimeout(() => {
        const shim = getShim();
        const curId = shim?.model.get('size_qty_id');
        const row = bankRows.find((r) => r.id == curId);
        if (row) void selectBankConfig(row.id, row.bank_width, row.bank_height, false);
      }, 500);
    });
    return () => {
      window.clearTimeout(timer);
      off();
    };
  }, [getShim, bankRows, selectBankConfig]);

  // section-resize listeners → checkSectionWidthHeight (legacy
  // changed:tunnel_<tid>:sizes + changed:secrion_<sid>:casing_insulation_id).
  useEffect(() => {
    const shim = getShim();
    const tunnelId = num(activate.section?.tunnel_id);
    const sectionId = num(activate.section?.id);
    const offs: Array<() => void> = [];
    const handler = () => {
      // re-pull sizes against the new section dims + re-rate (only if armed —
      // a section resize off the board IS a user action, so we honour it).
      userArmed.current = userArmed.current || !!shim?.model.get('master_filter_id');
      void reloadSizes().then(() => {
        void getFilterData();
        drawBankPlacement();
      });
    };
    if (tunnelId != null)
      offs.push(
        ahuBoardBus.on(
          `changed:tunnel_${tunnelId}:sizes` as `changed:tunnel_${number}:sizes`,
          handler
        )
      );
    if (sectionId != null)
      offs.push(
        ahuBoardBus.on(
          `changed:secrion_${sectionId}:casing_insulation_id` as `changed:secrion_${number}:casing_insulation_id`,
          handler
        )
      );
    return () => offs.forEach((off) => off());
  }, [activate.section, getShim, reloadSizes, getFilterData, drawBankPlacement]);

  // -------------------------------------------------------------------------
  // select availability (legacy checkSelectButtonAvabialty)
  // -------------------------------------------------------------------------
  // js:418-447. This used also to be called from exactly ONE place (the
  // has_frame branch), so Select enablement was effectively static for the
  // session; the count subscription above now re-runs it whenever a count
  // lands, which is when legacy ran it.
  const [navWarning, setNavWarning] = useState<Record<string, boolean>>({});
  // local mirror of the #select gate. `setSelectEnabled` is the chassis'
  // setter (ComponentSelectorDialog owns the state), so the dialog cannot read
  // it back — but legacy toggles `.filter_has_changes.visible` off the SAME
  // predicate (js:427-431), so it has to be readable here too.
  const [selectAvailable, setSelectAvailable] = useState(false);
  const warnTimer = useRef<number | undefined>(undefined);
  const checkSelectAvailability = useCallback(() => {
    const shim = getShim();
    if (!shim) return;
    const m = shim.model;
    const filterCount = shim.catalogueFields.master_filter_id.cmsPages.cms_count;
    const frameCount = shim.catalogueFields.master_frame_id.cmsPages.cms_count;
    const hasFrame = m.get('has_frame');
    const frameLoadingId = m.get('frame_loading_id');
    const filterOk = filterCount === 1 || !!m.get('master_filter_id');
    const frameOk = frameCount === 1 || !hasFrame;
    // js:423/426 `inValidSelection` — the pinned row is legacy's
    // `li.selected.non-clickable`, i.e. a saved configuration that is no longer
    // offered. Select stays greyed until the user picks a real one.
    const invalidSelection = !!bankById && bankById.id == m.get('size_qty_id');
    const available =
      filterOk && frameOk && (!!frameLoadingId || !hasFrame) && !invalidSelection;
    setSelectEnabled(available);
    setSelectAvailable(available);
    // js:433-446 — both red-tab checks are on a 1s timer so they do not flash
    // while a count round-trip is in flight.
    window.clearTimeout(warnTimer.current);
    warnTimer.current = window.setTimeout(() => {
      setNavWarning({
        frame: !!m.get('master_filter_id') && !frameOk,
        mounting: filterOk && frameOk && !frameLoadingId && !!hasFrame,
      });
    }, 1000);
  }, [getShim, setSelectEnabled, bankById]);

  useEffect(() => () => window.clearTimeout(warnTimer.current), []);
  // the arrow debounce resolves into selectBankConfig, which does a NON-silent
  // model.set — once restoreSeam() has flipped shim.rate false that reaches the
  // form's autosave, so a keypress 300ms before Cancel would PATCH the size the
  // user just cancelled.
  useEffect(() => () => window.clearTimeout(arrowTimer.current), []);
  // the pin is part of the gate above, so re-run when it lands or clears.
  useEffect(() => {
    checkSelectAvailability();
  }, [bankById, checkSelectAvailability]);

  // legacy `update:count:<model_id>:<baseUrl>` listeners (filter.selector.js
  // :196-225) repaint the "n / N" badge, the output pane and the Select gate
  // whenever a count round-trip lands. This dialog is mounted outside the
  // form's tree, so `useCatalogueFields` never re-renders it — subscribe to the
  // engines directly instead. Without this the badge keeps a stale value until
  // some unrelated interaction happens to re-render.
  useEffect(() => {
    const shim = getShim();
    if (!shim) return undefined;
    const offs = (['master_filter_id', 'master_frame_id'] as const)
      .map((t) =>
        shim.catalogueFields[t]?.subscribe?.(() => {
          checkSelectAvailability();
          bump();
        })
      )
      .filter(Boolean) as Array<() => void>;
    return () => offs.forEach((off) => off());
  }, [getShim, bump, checkSelectAvailability]);


  // -------------------------------------------------------------------------
  // §30.4 chassis fan-out: Select / Cancel / Rate
  // -------------------------------------------------------------------------
  // SELECT (legacy selectFilter): diff vs init → current_model PATCH → restore
  // seam → form render → conditional validateOnSectionChange.
  useEffect(() => {
    const key = `filter_${modelId}:selector:select` as `${string}_${number}:selector:select`;
    const off = ahuBoardBus.on(key, () => {
      void selectFilter();
    });
    return off;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId]);

  const selectFilter = useCallback(() => {
    const shim = getShim();
    if (!shim) {
      ahuBoardBus.emit('rating_mode:hide');
      return;
    }
    selectPressed.current = true;
    shim.rate = false; // legacy filterView.rate = false BEFORE diff/save
    const m = shim.model;
    const cur = m.toJSON();
    const data: Record<string, unknown> = {};
    const diffKeys = [
      'filter_params',
      'frame_params',
      'master_filter_id',
      'has_frame',
      'master_frame_id',
      'max_different_sizes',
    ];
    diffKeys.forEach((k) => {
      if (cur[k] !== init.current[k]) data[k] = cur[k];
    });
    if (cur.size_qty_id != init.current.size_qty_id)
      data.size_qty_id = num(cur.size_qty_id);
    if (
      JSON.stringify(allowableSizes.current) !== init.current.allowable_filter_sizes
    )
      data.allowable_filter_sizes = JSON.stringify(allowableSizes.current);
    // the legacy .model_field sweep (numeric/null normalization).
    [
      'air_stream_id',
      'frame_loading_id',
      'airflow_actual',
      'cost_freight_user',
      'cost_product_user',
      'net_price_freight_user',
      'net_price_product_user',
      'mounting_angle',
      'arrangement_id',
      'center_high',
      'height_from_floor',
      'distance_from_section_ups',
      'selection_condition',
      'min_face_velocity',
      'max_face_velocity',
      'supplied_by_id',
      'qty_of_sets',
      'pricing_method_id',
      'final_recommended_apd',
    ].forEach((name) => {
      const val = cur[name];
      if (val == init.current[name]) return;
      if (name === 'air_stream_id' || name === 'frame_loading_id')
        data[name] = val || null;
      else if (
        name === 'airflow_actual' ||
        name === 'cost_freight_user' ||
        name === 'cost_product_user' ||
        name === 'net_price_freight_user' ||
        name === 'net_price_product_user'
      )
        data[name] = val ? num(val) : null;
      else if (name === 'mounting_angle') data[name] = val && num(val) ? num(val) : 0;
      else data[name] = val;
    });

    if (Object.keys(data).length) {
      // legacy current_model.save(data,{patch:true}) — direct PATCH (not the
      // §49.2 debounce). Reuse the same concrete-row mutation the auto-save
      // pipeline uses, then xhr-echo into the model (no re-save loop).
      //
      // NOT awaited: legacy fires this save and hangs the validate off its
      // `.then()` while selectFilter runs straight on to the close
      // (filter.selector.js:761-767 → :798). Awaiting it here is what kept the
      // dialog on screen for a whole round-trip after Select. The echo lands on
      // the FORM's model, which lives on the board and outlives this dialog.
      void dispatch(
        ahuBoardFormsApi.endpoints.patchConcreteRow.initiate({
          plural: 'filters',
          id: modelId,
          data,
        })
      )
        .unwrap()
        .then((resp) => {
          m.set(resp as Record<string, unknown>, { xhr: true });
          // legacy: ahu.validateOnSectionChange({componentId}) when master/height/
          // distance changed.
          if (
            data.master_filter_id ||
            data.master_frame_id ||
            data.height_from_floor ||
            data.distance_from_section_ups
          ) {
            void dispatch(validateOnNewComponent(modelId));
          }
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[filter selector] selectFilter save failed', err);
        });
    }
    // Dev-sync J6 — the section persist (filter.selector.js:774-796):
    // season_id (Number || null) + the four staged entering temps VERBATIM,
    // then LADB/LAWB DERIVED = the entering pair per season (a filter passes
    // air through unchanged; the draft's own ladb/lawb copies are never
    // user-edited — the output pane is disabled/unreachable). Legacy runs
    // ONE PATCH via `section.set(...)` → the section model's autosave
    // (filter sections are outside the plenum/evap commit gate); its success
    // side-effects: the `changed:section` event when the _summer entering
    // pair is in the payload (section.js:231-234). checkChangesAndValidate
    // no-ops on air keys (no air field is in SECTION_VALIDATED_FIELDS) — not
    // dispatched.
    {
      const sec = activate.section as
        | ({ id?: number; tunnel_id?: number } & Record<string, unknown>)
        | undefined;
      const draft = sectionDraft.current;
      if (sec?.id != null) {
        const sectionSaveData: Record<string, unknown> = {
          season_id: Number(draft.season_id) || null,
        };
        (
          ['eadb_temp_summer', 'eawb_temp_summer', 'eadb_temp_winter', 'eawb_temp_winter'] as const
        ).forEach((field) => {
          sectionSaveData[field] = draft[field] ?? null;
        });
        sectionSaveData.ladb_temp_summer = sectionSaveData.eadb_temp_summer;
        sectionSaveData.lawb_temp_summer = sectionSaveData.eawb_temp_summer;
        sectionSaveData.ladb_temp_winter = sectionSaveData.eadb_temp_winter;
        sectionSaveData.lawb_temp_winter = sectionSaveData.eawb_temp_winter;
        // legacy `section.set(...)` no-ops entirely when nothing differs
        // (Backbone change-set empty → no autosave PATCH) — same gate here.
        const changedKeys = Object.keys(sectionSaveData).filter(
          (k) => String(sectionSaveData[k] ?? '') !== String(sec[k] ?? '')
        );
        if (changedKeys.length) {
          const sectionId = sec.id;
          const summerEnteringChanged =
            changedKeys.includes('eadb_temp_summer') ||
            changedKeys.includes('eawb_temp_summer');
          queueSectionPatch({
            dispatch,
            sectionId,
            tunnelId: Number(sec.tunnel_id),
            changes: sectionSaveData,
            onSuccess: () => {
              // section.js:231-234 — only a changed _summer entering pair
              // fires the live-chart refresh event.
              if (summerEnteringChanged) {
                ahuBoardBus.emit(`changed:section:${sectionId}:eadb_temp:eawb_temp`);
              }
            },
          });
        }
      }
    }
    // shim #8 restore + form render (legacy restores catalogueSelector then
    // filterView.render()). NB §33.7: NO minRequiredLengthChange here.
    //
    // Reached SYNCHRONOUSLY on the Select click — every write above is issued
    // and left in flight, exactly as legacy leaves them (filter.selector.js
    // :761-798). The dialog is gone by the time any of them lands.
    restoreSeam();
    ahuBoardBus.emit('rating_mode:hide');
  }, [getShim, dispatch, modelId, restoreSeam, activate]);

  // CANCEL (legacy cancelChanges + cancel:dialog:changes): rollback the model
  // to init (no select_pressed) + restore seam + form render.
  useEffect(() => {
    const cancel = () => {
      const shim = getShim();
      if (shim && !selectPressed.current) {
        // legacy: filterView.model.set(init) + current_model.set(init).
        // ONLY a local set (NO PATCH) — the preview round-trips already
        // persisted server-side; rollback to init is in-memory display state.
        shim.model.set({ ...init.current }, { silent: true });
        // …but legacy's set is NON-silent, and the change:<params> event is
        // what makes the catalogue engines re-seed from the restored params
        // (catalogueField.ts:114-123 → the form's Manufacturer/Type/Series
        // selects and its count badge). The silent set skipped that, so after
        // Cancel the FORM kept the abandoned dialog values. We fire the
        // backflow explicitly rather than un-silencing the set, which would
        // also re-open the autosave path.
        (
          [
            ['master_filter_id', 'filter_params'],
            ['master_frame_id', 'frame_params'],
          ] as const
        ).forEach(([target, paramsField]) => {
          shim.catalogueFields[target]?.resetFilter(
            parseJSON(init.current[paramsField]),
            true
          );
        });
      }
      restoreSeam();
    };
    const key = `filter_${modelId}:selector:cancel` as `${string}_${number}:selector:cancel`;
    const offs = [
      ahuBoardBus.on(key, cancel),
      ahuBoardBus.on('cancel:dialog:changes', cancel),
    ];
    return () => offs.forEach((off) => off());
  }, [modelId, getShim, restoreSeam]);

  // RATE button (legacy filter_<id>:selector:rate → self.rate, which is
  // getFilterData). #rate is disabled until armed; the chassis still routes it.
  useEffect(() => {
    const key = `filter_${modelId}:selector:rate` as `${string}_${number}:selector:rate`;
    const off = ahuBoardBus.on(key, () => {
      userArmed.current = true;
      void getFilterData();
    });
    return off;
  }, [modelId, getFilterData]);

  // -------------------------------------------------------------------------
  // how_to popup (legacy openHowTo / closeHowTo — .filter-how-to-popup)
  // -------------------------------------------------------------------------
  const openHowTo = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setHowToOpen((o) => !o);
  }, []);

  // -------------------------------------------------------------------------
  // RENDER (legacy template) — initial outputs from STORED model values
  // -------------------------------------------------------------------------
  const shim = getShim();
  if (!shim || !filterSizes.length) {
    return <div className="preloader dotted-preloader active" />;
  }
  const m = shim.model;
  const model = m.toJSON();
  const filterParams = parseJSON(model.filter_params);
  const frameParams = parseJSON(model.frame_params);
  const filterCount = shim.catalogueFields.master_filter_id.cmsPages.cms_count;
  const allCount = shim.catalogueFields.master_filter_id.cmsPages.cms_full_count;
  const frameCount = shim.catalogueFields.master_frame_id.cmsPages.cms_count;
  const allFrameCount = shim.catalogueFields.master_frame_id.cmsPages.cms_full_count;
  // Legacy's two count paths, and the difference between them is the whole bug:
  // the TEMPLATE blanks a falsy count (tmpl:81/:462) but only ever paints the
  // pre-count `undefined`, while the `update:count:*` listeners that run for the
  // rest of the dialog's life write `innerHTML = count` flat out — no falsy
  // guard, no master check (filter.selector.js:204, :219). So once a count has
  // been fetched legacy shows it, and 0 is a number like any other.
  const filterCountFetched =
    shim.catalogueFields.master_filter_id.cmsPages.cms_count_fetched;
  const frameCountFetched =
    shim.catalogueFields.master_frame_id.cmsPages.cms_count_fetched;
  const hasFrame = !!model.has_frame;
  const masterFilterId = model.master_filter_id;
  const curSizeQty = model.size_qty_id;
  const arrangements = Array.isArray(model.arrangements)
    ? (model.arrangements as Array<{ id: number; name?: string }>)
    : [];

  const sel = (v: unknown) => (v == null || v === '' ? '' : String(v));

  // computed outputs (legacy render_output) — from the STORED model values.
  const fp = filterParams;
  const availW = availableWidthHeight.current?.available_width;
  const availH = availableWidthHeight.current?.available_height;
  const sectionSizes =
    availW != null && availH != null
      ? `${(+availW).toFixed(2)} x ${(+availH).toFixed(2)}`
      : '';
  const faceArea =
    widthNominal.current && heightNominal.current
      ? (widthNominal.current * heightNominal.current) / 144
      : '';
  // filter.selector.js:1081 — the INITIAL half's placeholder is "- - " WITH a
  // trailing space; only the FINAL half is the bare "- -".
  const apdText =
    !model.initial_apd && !model.final_recommended_apd
      ? ''
      : `${model.initial_apd ? (+model.initial_apd).toFixed(2) : '- - '} / ${
          model.final_recommended_apd ? (+(model.final_recommended_apd as number)).toFixed(2) : '- -'
        }`;
  const filtersFrames = transformModels(
    (model.filter_frame ?? {}) as Record<string, Record<string, unknown>>,
    model,
    filterSizes
  );

  return (
    /* Backbone `View.className` is a WRAPPER (filter.selector.js:52
       `className: "filter_new_dialog"`), so legacy DOM is
       `div.filter_new_dialog > [ #filter_new , .filter.output-part ,
       .filter_has_changes ]` — three siblings (tmpl:1, :829, :832) — and the
       port collapsed all three onto one node.
       ★ RESTORING the wrapper was tried and REVERTED (QA 2026-08-31: "empty
       space between input and output parts"). The height chain does not
       survive it here: `.filter_new_dialog { height:100% }` (common.css:24464)
       resolves against `#rating_mode_filter_content { height:100% }`
       (rating_mode_filter.scss:2), so a separate `#filter_new` picks up
       `.input-part { height:100% }` (:18709) and becomes a FULL-HEIGHT box,
       pushing the sibling `.output-part` (also `height:100%`, :24701) a whole
       container-height down. The collapse makes both declarations land on one
       box, which is what actually renders correctly under the React chassis.
       Keep it collapsed. Verified harmless for CSS matching: every
       `.filter_new_dialog …` rule (common.css:24464-24521) is a plain
       descendant selector — none uses `>`, `+`, `~`, `:first-child` or
       `:last-child` — so they all still match with the panes nested. */
    <div
      id="filter_new"
      className="filter input-part aaa row filter-rating-mode filter_new_dialog"
    >
      {/* ===== Input nav (Filter / Frame / Mounting / Pricing / How to) ===== */}
      <div className="col-xs-1 nav nav-tabs">
        <ul className="col-xs-12">
          <li className="cursor-default">Input</li>
          {(['filter', 'frame', 'mounting', 'performance', 'pricing'] as InputTab[]).map((t) => (
            <li
              key={t}
              /* checkSelectButtonAvabialty (js:433-446) flags the Frame and
                 Mounting tabs red when the selection is incomplete
                 (common.css:24030 `li.warning a { color:#dc362e }`). */
              className={`${t === 'filter' ? 'ahu-Filter_Filter' : t === 'frame' ? 'ahu-Filter_Frame' : t === 'mounting' ? 'ahu-FilterMounting' : t === 'performance' ? 'ahu-Filter_Performance' : 'ahu-Filter_Pricing'} input-nav-item${inputTab === t ? ' selected' : ''}${navWarning[t] ? ' warning' : ''}`}
              data-name={t}
              onClick={() => changeInputTab(t)}
            >
              {/* legacy renders href="#ahu-Filter_X" (bootstrap's tab plugin
                  preventDefaults it). Without an href the anchor gets the text
                  cursor rather than the pointer — nothing in the cascade sets
                  `cursor` on these. */}
              <a
                href={`#ahu-Filter_${t === 'filter' ? 'Filter' : t === 'frame' ? 'Frame' : t === 'mounting' ? 'Mounting' : t === 'performance' ? 'Performance' : 'Pricing'}`}
                onClick={(e) => e.preventDefault()}
              >
                {t === 'filter' ? 'Filter' : t === 'frame' ? 'Frame' : t === 'mounting' ? 'Mounting' : t === 'performance' ? 'Performance' : 'Pricing'}
              </a>
            </li>
          ))}
          {/* ★ DELIBERATE deviation from legacy. filter.selector.tmpl:20 has a
              trailing <i class="icon-info"> after the "How to" anchor, and this
              port carried it (dev-sync F Stage 1 dropped it, Stage 2 put it
              back). Removed on request — the menu is now a plain text button,
              matching the Fan selector's `.fan-how-to-menu`, which has never had
              an icon (FanSelector.tsx:4693). The legacy rule
              `.filter_new_dialog .filter-how-to-menu i` (common.css:24513) is
              left in place but no longer matches anything here; the li is
              `display:flex` with one child, so nothing else shifts. */}
          {/* updateHowToState (js:1988-1997): red + bold when there are neither
              bank configurations nor filters to choose from. */}
          <li
            className={`filter-how-to-menu${!bankRows.length && !filterCount ? ' how-to-warning' : ''}`}
            data-name="how_to"
            onClick={openHowTo}
          >
            <a href="javascript:void(0)">How to</a>
          </li>
        </ul>
      </div>

      {/* ===== Content ===== */}
      <div className="col-xs-11 content-data tab-content">
        {/* Bank configuration + placement (shared header) */}
        {/* legacy `activateInputLabel` (filter.selector.js:1280) stamps data-tab
            with the active input tab; common.css:24230/:24234 use it to fade the
            block out on Frame (when the model has no frame) and on Performance.
            Without the attribute `hide-for-frame` can never match. */}
        {/* This block is also the smart list's `smartListSibling`
            (filter.selector.js:916): catalogue_selector.js:237 hides it while
            the list is rotated open and :251 brings it back. It is
            position:absolute at top:55px (common.css:24221) — i.e. it sits over
            the smart list's own rows — so leaving it up is what pushed the
            opened list out of place. */}
        {/* `hide` lands 50ms AFTER the list opens and comes off the instant the
            close starts (:237 / :251), so the block is on screen for the whole
            `animate-rotate` window in both directions — that is what gives the
            close its flip-in. */}
        <div
          id="bank-configuration-draw-block"
          data-tab={inputTab}
          className={`bank-configuration-draw-block padding-right-0${hasFrame ? '' : ' hide-for-frame'}${smartList && !rotating ? ' hide' : ''}${rotating ? ' animate-rotate' : ''}`}
        >
          <div className="flexed">
            <div>
              <div className="bank-configuration-label">
                Bank Configuration Wide x High{' '}
                <span
                  className="ka-has-tooltip"
                  tooltip="Available Bank Configurations are subject to all set conditions."
                >
                  <i className="icon-info" />
                </span>
              </div>
              <div className="bank-configuration">
                <ul
                  className="scrollable"
                  tabIndex={0}
                  ref={bankUlRef}
                  onScroll={onBankScroll}
                  onKeyDown={onBankKeydown}
                >
                  {/* tmpl:42-45 — the saved-but-no-longer-offered configuration
                      is pinned at the head, selected and NOT clickable. */}
                  {bankById && (
                    <li
                      data-size={bankById.wide_high}
                      data-index={-1}
                      data-id={bankById.id}
                      data-height={bankById.bank_height}
                      data-width={bankById.bank_width}
                      className={`${bankById.id == curSizeQty ? 'selected ' : ''}non-clickable`}
                    >
                      {bankById.wide_high}
                    </li>
                  )}
                  {bankRows.map((r, idx) => (
                    <li
                      key={r.id}
                      data-size={r.wide_high}
                      data-index={idx}
                      data-id={r.id}
                      data-height={r.bank_height}
                      data-width={r.bank_width}
                      className={r.id == curSizeQty ? 'selected' : ''}
                      onClick={() =>
                        void selectBankConfig(r.id, r.bank_width, r.bank_height, true)
                      }
                    >
                      {r.wide_high}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="w-263">
              <div className="bank-configuration-label ml-10">Bank Placement</div>
              <div className="bank-configuration-draw row">
                <div>
                  <div className="airflow-area" id="airflow_area">
                    <div className="filter-by-height" id="filter_by_height" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ----- FILTER input tab ----- */}
        <div
          className={`tab-pane input-tab-pane scrollable-content float-left${inputTab === 'filter' ? ' in active' : ''}`}
          id="ahu-Filter_Filter"
          name="ahu-Filter_Filter"
        >
          <div className="col-xs-12 sub-tab-menu nav nav-tabs">
            <div className="row">
              <div className="col-xs-12">
                <ul>
                  <div className="col-xs-5 filter-list-count-block min-width-44">
                    <div className="row">
                      <div className="col-lg-12">
                        {/* an invisible 31px hit-shield across the middle of the
                            row (common.css:23967, `pointer-events:auto;
                            cursor:default`) — without it the Smartlist button's
                            cursor and click target run the full width. */}
                        <span className="no-rotate" />
                        <button
                          name="filter"
                          data-name="filter"
                          className="btn btn-sync vis-filter clickable"
                          onClick={() => void toggleSmartList('filter')}
                        >
                          <span className="filters_search_result text-right">
                            <span
                              id="available_filtr_qty"
                              className={filterCount ? '' : 'warning'}
                            >
                              {/* tmpl:81 holds the slot open with four &nbsp; —
                                  plain JS spaces collapse to zero width. And
                                  with a master selected legacy prints a real
                                  `0` rather than blanking (js:804/817).
                                  `filterCountFetched` is the third case and in
                                  practice the usual one: a count that has come
                                  back is printed as-is, 0 included (js:204), so
                                  the four-&nbsp; slot only ever covers the
                                  window before the first count lands. */}
                              {filterCountFetched || masterFilterId
                                ? filterCount
                                : '\u00a0\u00a0\u00a0\u00a0'}
                            </span>
                            <span> / {allCount}</span>
                          </span>
                          {/* legacy changeSubTab (js:1533-1546) hides the
                              Smartlist label and Reset on the Conditions
                              sub-tab. */}
                          <i className={`icon-list btn-icon-sync${filterSubTab === 'conditions' ? ' hide' : ''}`}>
                            Smartlist
                          </i>
                        </button>
                        <span
                          id="filters_reset_btn"
                          className={`icon-refresh-1 filters_reset reset-btn text-right${filterSubTab === 'conditions' ? ' hide' : ''}`}
                          onClick={() => resetCatalogue()}
                        >
                          Reset
                        </span>
                      </div>
                    </div>
                  </div>
                </ul>
                <ul className="col-xs-6 text-right more_tabing" style={{ height: '29px' }}>
                  <li className={`filter_selection selection-tab${filterSubTab === 'selection' ? ' in active' : ''}`}>
                    <a tabIndex={-1} onClick={() => setFilterSubTab('selection')}>Selection&nbsp;</a>
                  </li>
                  <li className={`filter_conditions conditions-tab${filterSubTab === 'conditions' ? ' in active' : ''}`}>
                    <a tabIndex={-1} onClick={() => setFilterSubTab('conditions')}>Conditions&nbsp;</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          {/* legacy tmpl:101-476 — ONE `.more-tab-content` holds both the
              params sub-tabs and `#filter_smart_list` as siblings, and
              rotateOpen only un-hides the list (catalogue_selector.js:241). The
              params column is never taken down, which is why the left half of
              the pane stays on screen with the list open. */}
          <div className="more-tab-content">
            <FilterParamsPanel
              collections={collections}
              params={fp}
              model={model}
              subTab={filterSubTab}
              selectionCondition={selectionCondition}
              allowableSizes={allowableSizes.current}
              changedAllowable={changedAllowable.current}
              filterSizes={filterSizes}
              sizesPopupOpen={sizesPopupOpen}
              setSizesPopupOpen={setSizesPopupOpen}
              numRepaint={numRepaint.current}
              onParamsField={onParamsField}
              onModelField={onModelField}
              toggleAllowable={toggleAllowable}
              applyAllowableSizes={applyAllowableSizes}
              sel={sel}
            />
            {smartList === 'filter' && (
              <SmartListPanel
                side="filter"
                rotating={rotating}
                rows={smartRows}
                loading={smartLoading}
                listRef={filterRowsRef}
                onPick={(row) => pickSmartRow(row, 'filter')}
              />
            )}
          </div>
        </div>

        {/* ----- FRAME input tab ----- */}
        <div
          className={`tab-pane input-tab-pane scrollable-content filter-frame-tab-pane float-left${inputTab === 'frame' ? ' in active' : ''}`}
          id="ahu-Filter_Frame"
          name="ahu-Filter_Frame"
        >
          <div className="col-xs-12 nav sub-tab-menu">
            <div className="row">
              <div className="col-xs-12">
                <ul>
                  <div className="col-xs-5 frame-list-count-block">
                    <div className="row">
                      <div className="col-lg-12">
                        <span className="no-rotate" />
                        <button
                          name="frame"
                          data-name="frame"
                          className="btn btn-sync vis-frame clickable"
                          onClick={() => void toggleSmartList('frame')}
                        >
                          <span
                            className={`frames_search_result_span text-right${hasFrame ? '' : ' hide'}`}
                          >
                            {/* tmpl:462 prints the raw value — blank while the
                                count is still undefined, but a real `0` once
                                one has been fetched, and js:219 then rewrites
                                the slot with the raw number on every later
                                count. `|| ''` blanked both cases alike. */}
                            <span id="available_frame_qty">
                              {frameCountFetched ? frameCount : ''}
                            </span>
                            <span> / {allFrameCount}</span>
                          </span>
                          <i className="icon-list btn-icon-sync">Smartlist</i>
                        </button>
                        {/* tmpl:466 — the Frame tab's Reset was dropped in the
                            port entirely. Same binding as the filter side:
                            deselectModel() on master_frame_id. */}
                        <span
                          id="frames_reset_btn"
                          className="icon-refresh-1 frames_reset reset-btn text-right"
                          onClick={() => resetFrameCatalogue()}
                        >
                          Reset
                        </span>
                      </div>
                    </div>
                  </div>
                </ul>
                {/* tmpl:471 — empty, but it reserves the 29px row the frame
                    fields sit below. */}
                <ul className="col-xs-6 text-right more_tabing" style={{ height: '29px' }} />
              </div>
            </div>
          </div>
          {/* legacy tmpl:477-590 — same shape as the Filter tab above. */}
          <div className="more-tab-content">
            <FrameParamsPanel
              collections={collections}
              params={frameParams}
              hasFrame={hasFrame}
              visibleFrameParams={visibleFrameParams}
              onParamsField={onParamsField}
              onModelField={onModelField}
              sel={sel}
            />
            {smartList === 'frame' && (
              <SmartListPanel
                side="frame"
                rotating={rotating}
                rows={smartRows}
                loading={smartLoading}
                listRef={frameRowsRef}
                onPick={(row) => pickSmartRow(row, 'frame')}
              />
            )}
          </div>
          {/* keep an offscreen frame rows container so the seam target exists */}
          {smartList !== 'frame' && (
            <ul ref={frameRowsRef} className="hide catalogue-rows-container" />
          )}
        </div>

        {/* ----- MOUNTING input tab ----- */}
        <div
          className={`tab-pane input-tab-pane scrollable-content float-left${inputTab === 'mounting' ? ' in active' : ''}`}
          id="ahu-Filter_Mounting"
          name="ahu-Filter_Mounting"
        >
          <MountingPanel
            model={model}
            arrangements={arrangements}
            loadings={loadings}
            loadingWarning={!!navWarning.mounting}
            visibleLoadingIds={visibleLoadingIds}
            mountingAngleLocked={mountingAngleLocked}
            numRepaint={numRepaint.current}
            onModelField={onModelField}
            sel={sel}
          />
        </div>

        {/* ----- PERFORMANCE input tab (J6 — filter.selector.tmpl:759-830:
            Season select + season_id-gated Summer/Winter EADB/EAWB pairs;
            edits stage on sectionDraft, persisted only by Select. The
            OUTPUT-side Performance pane (output.tmpl:310-352) mirrors the same
            sectionDraft read-only and IS reachable — activateOutputLabel
            (filter.selector.js:1264-1275) resolves `href + "_output"` and
            `#ahu-Filter_Performance_output` exists; it is ported in
            OutputPanel below. (The earlier "unreachable in legacy, survey §B4"
            note here was wrong.) ----- */}
        <div
          className={`tab-pane input-tab-pane scrollable-content float-left${inputTab === 'performance' ? ' in active' : ''}`}
          id="ahu-Filter_Performance"
          name="ahu-Filter_Performance"
        >
          {(() => {
            const draft = sectionDraft.current;
            const seasonId = Number(draft.season_id) || null;
            const { showSummer, showWinter } = seasonShowFlags(seasonId);
            /* Requested layout: one row per season, the season name folded
               into the label (`Summer EADB / EAWB`) instead of the standalone
               `<h4>` heading row legacy puts above each pair
               (filter.selector.tmpl:775/:803). The `season-group-<season>`
               class stays on the row so the merged `[data-season]` CSS and
               `.season-group-* .label-part` padding still apply. */
            const tempPair = (season: 'summer' | 'winter') => (
              <>
                <div className={`row season-group-${season}`}>
                  <div className="col-xs-5 label-part">
                    <span>{season === 'summer' ? 'Summer' : 'Winter'}</span>{' '}
                    <span data-name={`eadb_temp_${season}`}>EADB</span> /{' '}
                    <span data-name={`eawb_temp_${season}`}>EAWB</span>
                    <span className="unit right temp">F</span>
                  </div>
                  <div className="col-xs-7 input-part">
                    <div className="row">
                      {(['eadb', 'eawb'] as const).map((half) => (
                        <div className="col-xs-6" key={`${half}_${season}`}>
                          {/* legacy binds `change .filter_section_field`
                              (blur/Enter), clamping THEN — commit-on-blur
                              keeps free typing (the coil Num convention).

                              DISPLAY is `fixed2`, i.e. always two
                              decimal places (`45.00`, not `45`). Legacy shows
                              the bare `{{section.eadb_temp_summer}}` and its
                              blur handler ends in `Number(num.toFixed(2))`
                              (filter.selector.js:705), which drops trailing
                              zeros — so this is a deliberate departure from
                              dev, requested in the thread, not a parity fix.
                              The value STAGED on sectionDraft is unchanged: a
                              clamped 2dp Number from onFilterSectionField, so
                              nothing about what Select persists moves. */}
                          <input
                            type="text"
                            style={{ width: '100%' }}
                            data-type="number"
                            autoComplete="off"
                            // -40, not legacy's 0: the section can now HOLD a
                            // sub-zero entering temperature, and the clamp
                            // below runs on every blur whether or not the field
                            // was edited, so a 0 floor here silently rewrote a
                            // -20 °F design day to 0.00 and Select persisted it.
                            data-min={MIN_AIR_TEMP_F}
                            data-max={150}
                            data-step={0.01}
                            name={`${half}_temp_${season}`}
                            className="filter_section_field search_field"
                            defaultValue={fixed2(draft[`${half}_temp_${season}`])}
                            onBlur={(e) => {
                              // legacy clamps then writes back (elem.val(value)).
                              const v = onFilterSectionField(
                                `${half}_temp_${season}`,
                                e.target.value,
                                MIN_AIR_TEMP_F,
                                150
                              );
                              e.currentTarget.value = v == null ? '' : v.toFixed(2);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.currentTarget.blur();
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            );
            return (
              <div
                /* The Filter tab's params column, verbatim — that is where
                   Airflow / Air Stream / Air PD Final come from. `min-width-44`
                   is what keeps `Air PD Final` + its `in WC` unit (and
                   `Summer EADB / EAWB`) on one line
                   (rating_mode_filter.scss:330 — min-width 44% + nowrap on the
                   label spans); the `col-md-5 col-lg-5` beside it are what
                   ahuBoardOverrides rule 1 matches on, so this column is capped
                   against `#bank-configuration-draw-block`, which is on screen
                   on this tab too. */
                className="col-xs-5 col-md-5 col-lg-5 min-width-44"
                data-season={deriveSeasonValue(globalDoc as ICmsAhuDoc | undefined, seasonId)}
                /* tmpl:593 — the same 41px the other input tabs start at, so
                   the four line up. (Briefly 14px and then 12px, to line the
                   first field up with the `Filter` nav item; reverted on
                   request along with hiding the bank block.) */
                style={{ marginTop: 41 }}
              >
                <AirflowFieldGroup
                  collections={collections}
                  model={model}
                  sel={sel}
                  onModelField={onModelField}
                />
                <div className="row">
                  <div className="col-xs-5 label-part">
                    <span data-name="season_id">Season</span>
                  </div>
                  <div className="col-xs-7 input-part">
                    <div className="row">
                      <div className="col-xs-12">
                        <select
                          name="season_id"
                          className="filter_section_field medium-input"
                          value={seasonId == null ? '' : String(seasonId)}
                          onChange={(e) => {
                            sectionDraft.current = {
                              ...sectionDraft.current,
                              season_id: parseInt(e.target.value, 10) || null,
                            };
                            bump();
                          }}
                        >
                          <option value="" className="hide">- -</option>
                          {cmsSeasonsByComponent(
                            globalDoc as ICmsAhuDoc | undefined,
                            'Filter'
                          ).map((r) => (
                            <option key={String(r.id)} value={String(r.id)}>
                              {String(r.name ?? '')}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                {showSummer && tempPair('summer')}
                {showWinter && tempPair('winter')}
              </div>
            );
          })()}
        </div>

        {/* ----- PRICING input tab ----- */}
        <div
          className={`tab-pane input-tab-pane scrollable-content float-left pricing_tab_wrap${inputTab === 'pricing' ? ' in active' : ''}`}
          id="ahu-Filter_Pricing"
          name="ahu-Filter_Pricing"
        >
          <PricingPanel
            model={model}
            pricingMethods={pricingMethods}
            onModelField={onModelField}
            sel={sel}
          />
        </div>
      </div>

      {/* ===== Output (tmpl:829 — a sibling in legacy; see the root comment
          for why it stays nested here) ===== */}
      <div className="filter output-part row filter-rating-mode">
        <OutputPanel
          outputTab={outputTab}
          setOutputTab={setOutputTab}
          hasFrame={hasFrame}
          collections={collections}
          filterParams={fp}
          frameParams={frameParams}
          model={model}
          sectionSizes={sectionSizes}
          faceArea={faceArea}
          apd={apdText}
          filtersFrames={filtersFrames}
          widthNominal={widthNominal.current}
          heightNominal={heightNominal.current}
          /* render_output's bank_wide_normal / bank_high_normal / high_normal
             (filter.selector.js:1085-1087) compare the AVAILABLE pair against
             the nominal width / model height, so the pane needs the raw pair,
             not just the "w x h" string. */
          availableWidth={availW}
          availableHeight={availH}
          /* output.tmpl:312-351 reads `section.*` — legacy passes
             `section: self.sectionDraft || {}` (filter.selector.js:1073).
             READ-ONLY here: the output pane never writes the draft. */
          section={sectionDraft.current}
        />
      </div>

      {/* tmpl:832 + common.css:24485-24499. Legacy drives this node with TWO
          INDEPENDENT classes and the port collapsed them into one:
           • `.active` (display:none → block) is added wherever a real user
             change lands (js:339, :581, :678, :886) and is never removed;
           • `.visible` (visibility:hidden → visible) is toggled ONLY by
             checkSelectButtonAvabialty (js:428/:431), on exactly the predicate
             that ungreys #select.
          So legacy shows the message only once the selection is complete
          enough to confirm, and while it is incomplete keeps the row laid out
          but invisible (display:block + visibility:hidden still reserves
          space). Emitting `active visible` together made dev3 print
          "Updated... Select to Confirm." next to a greyed-out SELECT. */}
      <div
        className={`filter_has_changes${userArmed.current ? ' active' : ''}${
          selectAvailable ? ' visible' : ''
        }`}
      >
        Updated... Select to Confirm.
      </div>

      {/* ===== how_to popup ===== */}
      {howToOpen && <HowToPopup onClose={() => setHowToOpen(false)} />}
    </div>
  );
};

/**
 * SHIM GATE (the §33.7 shim #8 cut's mount precondition).
 *
 * The filter selector is the one selector with NO staging clone: it drives the
 * LIVE form model through `window.__FORM_SHIM__[modelId]`. That shim is
 * registered by the host FilterForm in an effect that only fires once the
 * form's detail GET has resolved (useComponentForm — `shim` is null until then).
 *
 * The navy `#show_rating` opens the dialog in the SAME click that mounts the
 * host form (FormsPanel.showRating → setCurrentTab('components') +
 * openRatingRequest → the freshly mounted form's openRatingStub), so on the
 * FIRST open the selector mounted while the shim was still missing. The body
 * reads the shim through a plain `window` read, so nothing re-rendered it when
 * the shim landed — the preloader stayed up until the user closed and reopened
 * the dialog against the now-registered shim.
 *
 * Waiting here — rather than repainting the body when the shim appears — is
 * what keeps behaviour identical to that second open: every init effect in the
 * body runs exactly once, in order, with the shim present.
 */
export const FilterSelector = (props: SelectorTabProps) => {
  const { modelId } = props;
  const shimReady = useSyncExternalStore(subscribeFormShims, () =>
    Boolean(window.__FORM_SHIM__?.[modelId])
  );
  // ONE-WAY latch: the gate only ever holds the body back BEFORE its first
  // mount. A shim that disappears later (the host form unmounts on a
  // COMPONENT → ACCESSORIES tab roundtrip while the dialog is open) must NOT
  // tear the mounted body down — that would run its cleanup (the shim #8 seam
  // restore) and drop the dialog's state, which is not what happens today.
  const mounted = useRef(false);
  if (shimReady) mounted.current = true;
  // Same preloader the body shows while its own collections load, so the dialog
  // looks unchanged — it just stops being the terminal state.
  if (!mounted.current) return <div className="preloader dotted-preloader active" />;
  return <FilterSelectorBody {...props} />;
};

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
/** Handlebars `truncate_decimal` (helpers.js:159-171) — trunc to 2dp, then
 *  toFixed(2); empty string for blank/NaN. */
function truncateDecimal(v: unknown): string {
  if (v === '' || v === undefined || v === null || isNaN(Number(v))) return '';
  return (Math.trunc(Number(v) * 100) / 100).toFixed(2);
}

/** Handlebars `get_one_decimal` (helpers.js:154-157) — despite the name this is
 *  toFixed(2), which is why legacy prints `24.00` where the port printed
 *  `24.0`. */
function getOneDecimal(v: unknown): string {
  const n = +(v as number);
  return n ? n.toFixed(2) : '';
}

/** Two-decimal display for the Performance tab's EADB/EAWB inputs. Unlike
 *  `getOneDecimal` it keeps a legitimate 0 (`0.00`, the fields' data-min) and
 *  unlike `truncateDecimal` it rounds, so a value that skipped the blur clamp
 *  (the psychrometric re-rate handoff writes straight onto sectionDraft) is not
 *  shown a hundredth low. Blank stays blank. */
function fixed2(v: unknown): string {
  if (v === '' || v === null || v === undefined) return '';
  const n = Number(v);
  return Number.isNaN(n) ? '' : n.toFixed(2);
}

function isValidJSON(str: string): boolean {
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
}

/** legacy parseDimensions — "2-12W + 1-24W x 3-20H" → {W:[{count,value}],H:[...]}. */
function parseDimensions(input: string): {
  W: Array<{ count: number; value: string }>;
  H: Array<{ count: number; value: string }>;
} {
  const out: {
    W: Array<{ count: number; value: string }>;
    H: Array<{ count: number; value: string }>;
  } = { W: [], H: [] };
  input.split(/[x+]/).forEach((part) => {
    const match = part.trim().match(/(\d+)-(\d+)([WH])/);
    if (match) {
      const [, count, value, unit] = match;
      out[unit as 'W' | 'H'].push({ count: parseInt(count, 10), value });
    }
  });
  return out;
}

/** legacy transform_models — filter_frame map + per-size qty → result rows. */
function transformModels(
  frameMap: Record<string, Record<string, unknown>>,
  model: Record<string, unknown>,
  sizes: IFilterSizeRow[]
): Array<Record<string, unknown>> {
  const rows: Array<Record<string, unknown>> = [];
  sizes
    .map((el) => `${el.width}_${el.height}`)
    .forEach((size) => {
      const qty = model[`qty_${size}`];
      if (!qty) return;
      const data = frameMap[`filter_${size}`];
      if (!data) return;
      const [wide, high] = size.split('_');
      rows.push({
        qty,
        model: data.filter_model,
        mpn: data.filter_mpn,
        wide,
        high,
        frame_model: data.frame_model,
        frame_mpn: data.frame_mpn,
      });
    });
  return rows;
}

// ---------------------------------------------------------------------------
// sub-panels (kept in-file — they are pure presentational splits of the tmpl)
// ---------------------------------------------------------------------------
interface ParamsPanelCommon {
  collections: Record<string, Array<Record<string, unknown>>>;
  sel: (v: unknown) => string;
}

const Opt = ({
  rows,
  labelKey = 'name',
  valueKey = 'id',
  visibleIds,
}: {
  rows: Array<Record<string, unknown>>;
  labelKey?: string;
  valueKey?: string;
  /** changeSelectOptions (filter.selector.js:1891-1893) narrows a select by
   *  `option.addClass('hide')` on everything, then removing `hide` from the
   *  survivors — it never rebuilds the option list. `undefined` = no narrowing
   *  pass has run, so every option stays visible (previous behaviour). */
  visibleIds?: ReadonlySet<string> | null;
}) => (
  <>
    {(rows ?? []).map((r) => (
      <option
        key={String(r[valueKey])}
        value={String(r[valueKey])}
        className={
          visibleIds && !visibleIds.has(String(r[valueKey])) ? 'hide' : undefined
        }
      >
        {String(r[labelKey] ?? '')}
      </option>
    ))}
  </>
);

const FieldRow = ({
  label,
  unit,
  unitClass = 'unit right',
  rowStyle,
  bare,
  children,
}: {
  label: React.ReactNode;
  unit?: string;
  /** legacy uses `percent right` on the Pricing US$ units (tmpl:728/:744) —
   *  a different colour and size from `unit right` (common.css:18803 vs
   *  :21878 / :24051). */
  unitClass?: string;
  rowStyle?: React.CSSProperties;
  /** legacy rows that hold their own `.col-xs-6` pair have NO intermediate
   *  `.col-xs-12` (e.g. Face Velocity min-max, tmpl:330-342). Passing `bare`
   *  drops it so the inputs keep legacy's 15px offset and width. */
  bare?: boolean;
  children: React.ReactNode;
}) => (
  <div className="row" style={rowStyle}>
    <div className="col-xs-5 label-part">
      <span>{label}</span>
      {unit && <span className={unitClass}>{unit}</span>}
    </div>
    <div className="col-xs-7 input-part">
      <div className="row">
        {bare ? children : <div className="col-xs-12">{children}</div>}
      </div>
    </div>
  </div>
);

/**
 * Airflow / Air Stream / Air PD Final — the three model fields that legacy
 * renders at the foot of the Filter tab's Selection sub-tab
 * (filter.selector.tmpl:270-300). Requested move: they are now the head of the
 * **Performance** input tab, above Season and the Summer/Winter rows.
 *
 * Only the placement moved. The bindings are the originals — same `name`,
 * same `model_field` class, same keyed-on-model remount (so `getAHUAirflow`
 * and `getFilterData`'s echo still repaint the inputs), same clamps through
 * `onNumberBlur` → `onModelField`.
 */
const AirflowFieldGroup = ({
  collections,
  model,
  sel,
  onModelField,
}: ParamsPanelCommon & {
  model: Record<string, unknown>;
  onModelField: (name: string, raw: string | boolean) => void;
}) => (
  <>
    <FieldRow label="Airflow" unit="CFM">
      {/* getAHUAirflow (js:1481-1518) rewrites this from the Air Stream
          select, so it is keyed on the model value. No data-step ⇒
          integer + thousands separators (js:1427/:1452). */}
      <input
        key={`airflow-${String(model.airflow_actual ?? '')}`}
        name="airflow_actual"
        type="text"
        data-type="number"
        data-formatter_type="integer"
        className="w-100 model_field"
        defaultValue={formatIntegerDisplay(model.airflow_actual ?? '')}
        onBlur={onNumberBlur('airflow_actual', {}, onModelField)}
      />
    </FieldRow>
    <FieldRow label="Air Stream">
      <select
        className="params_filter model_field"
        value={sel(model.air_stream_id)}
        onChange={(e) => onModelField('air_stream_id', e.target.value)}
      >
        <option value="">- -</option>
        <Opt rows={collections.air_stream} />
      </select>
    </FieldRow>
    <FieldRow label="Air PD Final" unit="in WC">
      {/* getFilterData echoes final_recommended_apd back (js:1030), so
          keyed. data-min="0" is FALSY to `input.data("min")` — legacy
          enforces the max only (see numberField). */}
      <input
        key={`apd-${String(model.final_recommended_apd ?? '')}`}
        name="final_recommended_apd"
        type="text"
        data-type="number"
        data-step="0.01"
        className="model_field w-100 text-left"
        data-min="0"
        data-max="3"
        defaultValue={formatDecimalDisplay(model.final_recommended_apd ?? '')}
        onBlur={onNumberBlur(
          'final_recommended_apd',
          { min: 0, max: 3, step: 0.01 },
          onModelField
        )}
      />
    </FieldRow>
  </>
);

const FilterParamsPanel = ({
  collections,
  params,
  model,
  subTab,
  selectionCondition,
  allowableSizes,
  changedAllowable,
  filterSizes,
  sizesPopupOpen,
  setSizesPopupOpen,
  numRepaint,
  onParamsField,
  onModelField,
  toggleAllowable,
  applyAllowableSizes,
  sel,
}: ParamsPanelCommon & {
  params: Record<string, unknown>;
  model: Record<string, unknown>;
  subTab: 'selection' | 'conditions';
  selectionCondition: Array<Record<string, unknown>>;
  allowableSizes: IFilterSizeRow[];
  changedAllowable: IFilterSizeRow[];
  filterSizes: IFilterSizeRow[];
  sizesPopupOpen: boolean;
  setSizesPopupOpen: (o: boolean) => void;
  /** per-field remount nonce for the uncontrolled number inputs (see the
   *  numRepaint comment in FilterSelector). */
  numRepaint: Record<string, number>;
  onParamsField: (t: 'master_filter_id', name: string, raw: string) => void;
  onModelField: (name: string, raw: string | boolean) => void;
  toggleAllowable: (id: number | '', checked: boolean) => void;
  applyAllowableSizes: () => void;
}) => {
  const pf = (name: string) => (e: React.ChangeEvent<HTMLSelectElement>) =>
    onParamsField('master_filter_id', name, e.target.value);
  const allowableIds = new Set(changedAllowable.map((el) => el.id));
  /* the `.more-tab-content` wrapper is the caller's — it is shared with
     #filter_smart_list, as in tmpl:101. */
  return (
    <>
      <div className={`tab-pane scrollable-content float-left sub_tab${subTab === 'selection' ? ' in active' : ''}`} id="filter_selection" style={subTab === 'selection' ? undefined : { display: 'none' }}>
        <div className="col-xs-5 col-md-5 col-lg-5 min-width-44">
          <FieldRow label="Manufacturer">
            <select className="params_filter filter_params_field" value={sel(params.mfg_id)} onChange={pf('mfg_id')}>
              <option value="" className="hide">- -</option>
              <Opt rows={collections.manufacturer} />
            </select>
          </FieldRow>
          <FieldRow label="Type">
            <select className="params_filter filter_params_field" value={sel(params.type_id)} onChange={pf('type_id')}>
              <option value="" className="hide">- -</option>
              <Opt rows={collections.type} />
            </select>
          </FieldRow>
          <FieldRow label="Series">
            <select className="params_filter filter_params_field" value={sel(params.series_id)} onChange={pf('series_id')}>
              <option value="" className="hide">- -</option>
              <Opt rows={collections.series} labelKey="series" />
            </select>
          </FieldRow>
          <div className="row">
            <div className="col-xs-5 label-part">
              <span>MERV</span> | <span>Efficiency</span>
            </div>
            <div className="col-xs-7 input-part">
              <div className="row">
                <div className="col-xs-6">
                  <select className="params_filter filter_params_field" value={sel(params.merv_id)} onChange={pf('merv_id')}>
                    <option value="" className="hide">- -</option>
                    <Opt rows={collections.merv} labelKey="value" />
                  </select>
                </div>
                <div className="col-xs-6">
                  <select className="params_filter filter_params_field" value={sel(params.efficiency_id)} onChange={pf('efficiency_id')}>
                    <option value="" className="hide">- -</option>
                    <Opt rows={collections.efficiency} labelKey="value" />
                  </select>
                </div>
              </div>
            </div>
          </div>
          <FieldRow label="Depth" unit="in">
            <select className="params_filter filter_params_field" value={sel(params.depth_nominal_id)} onChange={pf('depth_nominal_id')}>
              <option value="" className="hide">- -</option>
              <Opt rows={collections.nominal_depth} labelKey="name" />
            </select>
          </FieldRow>
          <FieldRow label="UL Class">
            <select className="params_filter" value={sel(params.ul_class_id)} onChange={pf('ul_class_id')}>
              <Opt rows={collections.class} labelKey="value" />
            </select>
          </FieldRow>
          <div className="row">
            <div className="col-xs-5 label-part">
              <span>Supplied by</span> | <span>Sets</span>
            </div>
            <div className="col-xs-7 input-part">
              <div className="row">
                <div className="col-xs-10">
                  <select className="params_filter model_field" value={sel(model.supplied_by_id)} onChange={(e) => onModelField('supplied_by_id', e.target.value)}>
                    <Opt rows={collections.supplied_by} />
                  </select>
                </div>
                <div className="col-xs-2">
                  <input
                    type="text"
                    data-formatter_type="integer"
                    className="text-right w-100 model_field"
                    defaultValue={String(model.qty_of_sets ?? '')}
                    onBlur={(e) => onModelField('qty_of_sets', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
          {/* Airflow / Air Stream / Air PD Final used to close this sub-tab
              (legacy tmpl:270-300, with a 15px break off the catalogue group).
              They now live on the Performance input tab — see
              `AirflowFieldGroup`, rendered there. Requested move, not a parity
              fix. */}
        </div>
      </div>
      {/* ---- Conditions sub-tab (legacy #filter_conditions) ---- */}
      <div className={`tab-pane scrollable-content float-left sub_tab${subTab === 'conditions' ? ' in active' : ''}`} id="filter_conditions" style={subTab === 'conditions' ? undefined : { display: 'none' }}>
        <div className="col-xs-5 col-md-5 col-lg-5 min-width-44">
          {/* tmpl:330-342 — the col-xs-6 pair sits DIRECTLY in the .row;
              routing it through FieldRow's default .col-xs-12 shifted both
              inputs ~15px left and widened them. */}
          {/* js:613-625 — each half cross-writes the OTHER's data-min/data-max
              (already carried by the bindings below) and CLAMPS the partner
              value; both are therefore keyed so the partner input repaints. */}
          <FieldRow label="Face Velocity min - max" unit="fpm" bare>
            <div className="col-xs-6">
              <input key={`minfv-${String(model.min_face_velocity ?? '')}`} type="text" data-type="number" data-step="1" data-min="200" data-max={String(model.max_face_velocity ?? '')} className="text-right model_field w-100" defaultValue={formatIntegerDisplay(model.min_face_velocity ?? '')} onBlur={onNumberBlur('min_face_velocity', { min: 200, max: model.max_face_velocity, step: 1 }, onModelField)} />
            </div>
            <div className="col-xs-6">
              <input key={`maxfv-${String(model.max_face_velocity ?? '')}`} type="text" data-type="number" data-step="1" data-min={String(model.min_face_velocity ?? '')} data-max="750" className="text-right model_field w-100" defaultValue={formatIntegerDisplay(model.max_face_velocity ?? '')} onBlur={onNumberBlur('max_face_velocity', { min: model.min_face_velocity, max: 750, step: 1 }, onModelField)} />
            </div>
          </FieldRow>
          <FieldRow label="Dimensional Constraints">
            <select className="params_filter model_field" value={sel(model.selection_condition)} onChange={(e) => onModelField('selection_condition', e.target.value)}>
              <option className="hide" value="">- -</option>
              <Opt rows={selectionCondition} labelKey="name" valueKey="value" />
            </select>
          </FieldRow>
          <FieldRow label="Max number of sizes">
            {/* clamped to [1, min(allowable.length, 3)] in onModelField
                (js:657-665) and also rewritten by Apply on the allowable-sizes
                popup, so keyed. */}
            <input key={`maxsizes-${String(model.max_different_sizes ?? '')}-${numRepaint.max_different_sizes ?? 0}`} type="text" data-type="number" className="model_field w-100" defaultValue={formatIntegerDisplay(model.max_different_sizes ?? '')} onBlur={onNumberBlur('max_different_sizes', {}, onModelField)} />
          </FieldRow>
          <FieldRow label="Allowable filter sizes">
            <div className="multi-select-group">
              <button
                id="show_allowable_filter_sizes"
                type="button"
                className="multi-select-group-button icon-mounting icon-selection"
                tabIndex={-1}
                onClick={() => setSizesPopupOpen(!sizesPopupOpen)}
              />
              {sizesPopupOpen && (
                <div className="allowable_filter_sizes_list multi-select-list active">
                  <ul>
                    <li>
                      <label>
                        <input
                          type="checkbox"
                          className="allowable_filter_sizes"
                          checked={changedAllowable.length === filterSizes.length}
                          onChange={(e) => toggleAllowable('', e.target.checked)}
                        />
                        <span className="label">All</span>
                      </label>
                    </li>
                    {filterSizes.map((s) => (
                      <li key={s.id}>
                        <label>
                          <input
                            type="checkbox"
                            className="allowable_filter_sizes"
                            value={s.id}
                            checked={allowableIds.has(s.id)}
                            onChange={(e) => toggleAllowable(s.id, e.target.checked)}
                          />
                          <span className="label">{s.name}</span>
                        </label>
                      </li>
                    ))}
                    <li className="apply-btn-li">
                      <button
                        className={`btn${changedAllowable.length ? '' : ' non-clickable'}`}
                        id="apply-sizes"
                        onClick={() => applyAllowableSizes()}
                      >
                        Apply
                      </button>
                    </li>
                  </ul>
                </div>
              )}
              <div className="checked-values output allowable_filter_sizes_output">
                {allowableSizes.map((s) => (
                  <span key={s.id}>{s.name}</span>
                ))}
              </div>
            </div>
          </FieldRow>
        </div>
      </div>
    </>
  );
};

const FrameParamsPanel = ({
  collections,
  params,
  hasFrame,
  visibleFrameParams,
  onParamsField,
  onModelField,
  sel,
}: ParamsPanelCommon & {
  params: Record<string, unknown>;
  hasFrame: boolean;
  /** getFrameParamas (js:1919-1951) → changeSelectOptions(…, params_frame=true)
   *  survivors, keyed by frame param field name. */
  visibleFrameParams: Record<string, ReadonlySet<string>>;
  onParamsField: (t: 'master_frame_id', name: string, raw: string) => void;
  onModelField: (name: string, raw: string | boolean) => void;
}) => {
  const frameCol = (collections.frame ?? {}) as unknown as Record<
    string,
    Array<Record<string, unknown>>
  >;
  const pf = (name: string) => (e: React.ChangeEvent<HTMLSelectElement>) =>
    onParamsField('master_frame_id', name, e.target.value);
  const dis = hasFrame ? '' : ' non-clickable';
  /* wrapper is the caller's — shared with #frame_smart_list (tmpl:477). */
  return (
    <>
      <div className="tab-pane in active scrollable-content float-left sub_tab" id="frame_selection">
        <div
          className="col-xs-5 col-md-5 col-lg-5 min-width-44"
          style={{ marginTop: 8 }}
        >
          <FieldRow label="Include Frame">
            <input
              type="checkbox"
              name="has_frame"
              className="model_field"
              checked={hasFrame}
              onChange={(e) => onModelField('has_frame', e.target.checked)}
            />
          </FieldRow>
          <FieldRow label="Type">
            <select className={`frame_params_field${dis}`} value={sel(params.frame_type_id)} onChange={pf('frame_type_id')}>
              <option className="hide" value="">- -</option>
              <Opt rows={frameCol.type} visibleIds={visibleFrameParams.frame_type_id} />
            </select>
          </FieldRow>
          <FieldRow label="Manufacturer">
            <select className={`params_frame frame_params_field${dis}`} value={sel(params.mfg_id)} onChange={pf('mfg_id')}>
              <option className="hide" value="">- -</option>
              <Opt rows={frameCol.manufacturer} visibleIds={visibleFrameParams.mfg_id} />
            </select>
          </FieldRow>
          <FieldRow label="Series">
            <select className={`params_frame frame_params_field${dis}`} value={sel(params.series_id)} onChange={pf('series_id')}>
              <option value="" className="hide">- -</option>
              <Opt rows={frameCol.series} labelKey="series" visibleIds={visibleFrameParams.series_id} />
            </select>
          </FieldRow>
          <FieldRow label="Material">
            <select className={`params_frame frame_params_field${dis}`} value={sel(params.mtl_id)} onChange={pf('mtl_id')}>
              <option value="" className="hide">- -</option>
              <Opt rows={frameCol.mtl_gauge} visibleIds={visibleFrameParams.mtl_id} />
            </select>
          </FieldRow>
        </div>
      </div>
    </>
  );
};

const MountingPanel = ({
  model,
  arrangements,
  loadings,
  loadingWarning,
  visibleLoadingIds,
  mountingAngleLocked,
  numRepaint,
  onModelField,
  sel,
}: {
  model: Record<string, unknown>;
  arrangements: Array<{ id: number; name?: string }>;
  loadings: Array<Record<string, unknown>>;
  /** js:441 — `select[name=frame_loading_id].warning` (common.css:24473
   *  border-bottom-color:#dc362e) when the selection is otherwise complete but
   *  no loading is chosen. */
  loadingWarning: boolean;
  /** js:302-305 — the dependency-narrowed survivors (changeSelectOptions). */
  visibleLoadingIds: ReadonlySet<string> | null;
  /** js:1961 — changeDepth's lock (depth > 4 in OR arrangement 2). */
  mountingAngleLocked: boolean;
  /** per-field remount nonce for the uncontrolled number inputs. */
  numRepaint: Record<string, number>;
  onModelField: (name: string, raw: string | boolean) => void;
  sel: (v: unknown) => string;
}) => (
  /* tmpl:593 — `col-xs-5 col-md-5 col-lg-5 min-width-44` + margin-top:41px.
     Dropping the inline margin left Mounting/Pricing 41px higher than the
     Performance tab, so the four input tabs no longer line up. */
  <div className="col-xs-5 col-md-5 col-lg-5 min-width-44" style={{ marginTop: 41 }}>
    <FieldRow label="Arrangement">
      <select className="model_field" value={sel(model.arrangement_id)} onChange={(e) => onModelField('arrangement_id', e.target.value)}>
        <option className="hide" value="">- -</option>
        {arrangements.map((a) => (
          <option key={a.id} value={String(a.id)}>{a.name}</option>
        ))}
      </select>
    </FieldRow>
    <FieldRow label="Loading">
      <select
        className={`model_field${model.frame_loading_id ? '' : ' with_error'}${loadingWarning ? ' warning' : ''}`}
        value={sel(model.frame_loading_id)}
        onChange={(e) => onModelField('frame_loading_id', e.target.value)}
      >
        <option className="always-visible" value="">- -</option>
        <Opt rows={loadings} visibleIds={visibleLoadingIds} />
      </select>
    </FieldRow>
    <FieldRow label="From section upstream" unit="in">
      {/* keyed on the model value so a PROGRAMMATIC write repaints the
          uncontrolled input — the clear_min_ups_in_section floor (js:600-609)
          and getFilterData's echo both rewrite this field. */}
      <input
        key={`dfsu-${String(model.distance_from_section_ups ?? '')}-${numRepaint.distance_from_section_ups ?? 0}`}
        type="text"
        data-type="number"
        data-step="0.01"
        className="model_field w-100"
        defaultValue={formatDecimalDisplay(model.distance_from_section_ups ?? '0.00')}
        onBlur={onNumberBlur('distance_from_section_ups', { step: 0.01 }, onModelField)}
      />
    </FieldRow>
    <div className="row">
      <div className="col-xs-5 label-part">
        <span>Height from floor</span>
        <span className="unit right">in</span>
      </div>
      <div className="col-xs-7 input-part">
        <div className="row">
          <div className="col-xs-6">
            {/* getFilterData echoes height_from_floor back when Center is
                ticked (js:633-637), so this one is keyed too. */}
            <input
              key={`hff-${String(model.height_from_floor ?? '')}`}
              type="text"
              data-type="number"
              data-step="0.01"
              className={`text-right model_field w-100${model.center_high ? ' non-clickable' : ''}`}
              defaultValue={formatDecimalDisplay(model.height_from_floor ?? '')}
              onBlur={onNumberBlur('height_from_floor', { step: 0.01 }, onModelField)}
            />
          </div>
          <div className="col-xs-6 text-right center-row">
            <input type="checkbox" className="model_field" checked={!!model.center_high} onChange={(e) => onModelField('center_high', e.target.checked)} />
            <span className="label-part" style={{ position: 'relative', top: 3, left: 5 }}>Center</span>
          </div>
        </div>
      </div>
    </div>
    <FieldRow
      label={
        <>
          {'Mounting Angle '}
          {/* tmpl:676-683 — the hover explanation of the -90/0/+90 convention.
              Styled by common.css:24428-24460, dead without this markup. */}
          <span className="mounting_angle_tooltip">
            <i className="icon-info" />
            <span className="mounting_angle_tooltip_text hide">
              <span>-90 degrees, layed down horizontally, opposite to airflow</span>
              <span>0 degree, vertical</span>
              <span>+90 degrees, layed down horizontally, in direction of airflow</span>
            </span>
          </span>
        </>
      }
      unit="deg"
    >
      {/* js:1960/:628 zero this field from BOTH changeDepth and the
          arrangement change, so it is keyed on the model value. No data-step ⇒
          legacy formats it as an integer (js:1427). */}
      <input
        key={`mangle-${String(model.mounting_angle ?? '')}`}
        type="text"
        data-type="number"
        data-min="-90"
        data-max="90"
        className={`text-right model_field w-100${mountingAngleLocked ? ' non-clickable' : ''}`}
        defaultValue={formatIntegerDisplay(model.mounting_angle ?? '')}
        onBlur={onNumberBlur('mounting_angle', { min: -90, max: 90 }, onModelField)}
      />
    </FieldRow>
  </div>
);

const PricingPanel = ({
  model,
  pricingMethods,
  onModelField,
  sel,
}: {
  model: Record<string, unknown>;
  pricingMethods: Array<Record<string, unknown>>;
  onModelField: (name: string, raw: string | boolean) => void;
  sel: (v: unknown) => string;
}) => {
  const method = Number(model.pricing_method_id);
  const isUser = method === 2;
  const hidePricing = method === 1;
  return (
    /* tmpl:706 — margin-top:41px, same as Mounting. */
    <div
      className="col-xs-5 col-md-5 col-lg-5 min-width-44 float-left"
      style={{ marginTop: 41 }}
    >
      <FieldRow label="Pricing Method">
        <select className="search_field model_field" value={sel(model.pricing_method_id)} onChange={(e) => onModelField('pricing_method_id', e.target.value)}>
          <Opt rows={pricingMethods} />
        </select>
      </FieldRow>
      {!hidePricing && (
        <>
          {/* both are `input[data-type="number"][data-step="0.01"]`, so
              validateNumberField (js:1422-1473) formats them to 2 decimals
              with thousands separators. */}
          <FieldRow label="Material" unit="US$" unitClass="percent right">
            <input
              type="text"
              data-type="number"
              data-step="0.01"
              className="model_field search_field"
              style={{ width: '100%' }}
              defaultValue={formatDecimalDisplay((isUser ? model.cost_product_user : model.net_price_product_user) ?? '')}
              onBlur={onNumberBlur(isUser ? 'cost_product_user' : 'net_price_product_user', { step: 0.01 }, onModelField)}
            />
          </FieldRow>
          <FieldRow label="Freight" unit="US$" unitClass="percent right">
            <input
              type="text"
              data-type="number"
              data-step="0.01"
              className="model_field search_field"
              style={{ width: '100%' }}
              defaultValue={formatDecimalDisplay((isUser ? model.cost_freight_user : model.net_price_freight_user) ?? '')}
              onBlur={onNumberBlur(isUser ? 'cost_freight_user' : 'net_price_freight_user', { step: 0.01 }, onModelField)}
            />
          </FieldRow>
        </>
      )}
    </div>
  );
};

const SmartListPanel = ({
  side,
  rotating,
  rows,
  loading,
  listRef,
  onPick,
}: {
  side: 'filter' | 'frame';
  /** inside the 50ms `animate-rotate` window — see `runRotate`. */
  rotating: boolean;
  rows: IMasterRow[];
  /** a page request is in flight — legacy's `#list-preloader` window. */
  loading: boolean;
  listRef: React.Ref<HTMLUListElement>;
  onPick: (row: IMasterRow) => void;
}) => (
  /* legacy tmpl:425 / :571 — the smart list is a SIBLING of the params sub-tab
     inside the caller's `.more-tab-content` (so it picks up :21185
     margin-top:38px / :21188 > div margin-top:-27px) and carries
     `col-md-12 col-lg-7 col-lg-offset-5`. `#filter_smart_list` is
     position:absolute with no top/left (common.css:23887), so those grid
     classes are the only thing placing it: without them it spans the full
     width flush left instead of the right 58.33%. */
  <div
    className={`col-xs-12 col-md-12 col-lg-7 col-lg-offset-5 for_smart_list${rotating ? ' animate-rotate' : ''}`}
    id={`${side}_smart_list`}
  >
    <div>
    <div className={`${side}-list`}>
      <div className="col-xs-12 padding-right-0">
      <ul className="list-heading list-inline" id="smart-list-ul">
        {side === 'filter' ? (
          <li className="only-for-filter custom-columns">
            <h4 className="w-140">MANUFACTURER</h4>
            <h4 className="w-120">SERIES</h4>
            <h4 className="w-100">TYPE</h4>
            <h4 className="w-40 text-align-right">MERV</h4>
            <h4 className="w-65 text-align-right">EFF (%)</h4>
            <h4 className="w-60 text-align-right">DEPTH</h4>
          </li>
        ) : (
          <li className="only-for-frame custom-columns">
            <h4 className="w-160">MANUFACTURER</h4>
            <h4 className="w-160">SERIES</h4>
            <h4 className="w-200">MATERIAL</h4>
          </li>
        )}
      </ul>
      <ul
        id={`catalogue-rows-${side}-container`}
        className="list-rows scrollable catalogue-rows-container"
        ref={listRef}
      >
        {/* `load()` paints `#list-preloader` only while a page request is in
            flight and removes it on resolve (catalogue_selector.js:138-143), so
            a list that legitimately has no rows renders empty. */}
        {loading && !rows.length && (
          <div id="list-preloader" className="preloader dotted-preloader active" />
        )}
        {rows.map((row) => (
          <li key={row.id}>
            <ul
              name={String(row.id)}
              className={`table-item ${side}`}
              onClick={() => onPick(row)}
            >
              <li className="xxlong non-clickable text-overflow-unset">
                {/* catalogue_selector_row.tmpl:48-62. `fromRateDialog` is
                    `catalogueSelector.with_rating` (catalogue_selector.js:164)
                    and is TRUE here, so the rate-dialog width set applies —
                    w-140/120/100/40/65/60 with the EFF (%) column, not the
                    form's w-150/150/150/50/85. The port had the form widths and
                    was missing EFF entirely, so the rows did not line up with
                    the header this same component renders. */}
                {side === 'filter' ? (
                  <span className="only-for-filter-row custom-columns-row">
                    <span className="w-140">{String((row as Record<string, unknown>).mfg_name ?? '')}</span>
                    <span className="w-120">{String((row as Record<string, unknown>).series_name ?? '')}</span>
                    <span className="w-100">{String((row as Record<string, unknown>).type_name ?? '')}</span>
                    <span className="w-40 text-align-right">{String((row as Record<string, unknown>).merv_id ?? '')}</span>
                    <span className="w-65 text-align-right">{truncateDecimal((row as Record<string, unknown>).efficiency_value)}</span>
                    <span className="w-60 text-align-right">{String((row as Record<string, unknown>).depth_nominal ?? '')}</span>
                  </span>
                ) : (
                  <span className="only-for-frame-row custom-columns-row">
                    <span className="w-160">{String((row as Record<string, unknown>).mfg_name ?? '')}</span>
                    <span className="w-160">{String((row as Record<string, unknown>).series_name ?? '')}</span>
                    <span className="w-200">{String((row as Record<string, unknown>).mtl_name ?? '')}</span>
                  </span>
                )}
              </li>
            </ul>
          </li>
        ))}
      </ul>
      </div>
    </div>
    </div>
  </div>
);

/**
 * The OUTPUT mirror — a faithful port of filter.selector.output.tmpl (352 lines)
 * as rendered by render_output (filter.selector.js:1056-1100).
 *
 * Three panes, all three in the DOM at once exactly as Handlebars emits them
 * (output.tmpl:21, :230, :310); Bootstrap 3's `.tab-content > .tab-pane
 * {display:none}` / `.active {display:block}` does the switching, which is why
 * the panes MUST carry `tab-pane fade` and why the port's conditional render
 * (only the active pane, and without those classes) also lost
 * `#rating_mode_filter_content .output-part .tab-pane {margin-top:-2px}`
 * (common.css:24795).
 *
 * The Backbone `prefix` is "ahu-" on the board (filter.selector.js:806
 * `prefix: self.header ? "" : "ahu-"`), so the pane ids are
 * `ahu-Filter_{Filter,Frame,Performance}_output` and each pane also mirrors its
 * id into `name` (output.tmpl:22, :231, :311).
 */
const OutputPanel = ({
  outputTab,
  setOutputTab,
  hasFrame,
  collections,
  filterParams,
  frameParams,
  model,
  sectionSizes,
  faceArea,
  apd,
  filtersFrames,
  widthNominal,
  heightNominal,
  availableWidth,
  availableHeight,
  section,
}: {
  outputTab: OutputTab;
  setOutputTab: (t: OutputTab) => void;
  hasFrame: boolean;
  collections: Record<string, Array<Record<string, unknown>>>;
  filterParams: Record<string, unknown>;
  frameParams: Record<string, unknown>;
  model: Record<string, unknown>;
  sectionSizes: string;
  faceArea: number | '';
  apd: string;
  filtersFrames: Array<Record<string, unknown>>;
  widthNominal: number | null;
  heightNominal: number | null;
  availableWidth: number | undefined;
  availableHeight: number | undefined;
  section: Record<string, unknown>;
}) => {
  const frameCol = (collections.frame ?? {}) as unknown as Record<
    string,
    Array<Record<string, unknown>>
  >;

  // ── render_output's computed flags (filter.selector.js:1085-1092) ──────────
  // Legacy hands the template STRINGS here (`(+x).toFixed(2)` or ''), so
  // `available_width >= +width_nominal_value` is a string-vs-number compare
  // that JS coerces; ported as the numeric comparison it actually performs.
  // NB `bank_high_normal` and `high_normal` are the SAME expression in legacy
  // (js:1086-1087) — both against `height` (the MODEL height), not the nominal.
  const heightNum = model.height ? Number(model.height) : 0;
  const bankWideNormal = !availableWidth || availableWidth >= Number(widthNominal);
  const bankHighNormal = !availableHeight || availableHeight >= heightNum;
  const highNormal = bankHighNormal;
  // js:1092 — out-of-band face velocity paints red via
  // `.output-data input.has_error {color:#dc362e}` (common.css:24821-24823).
  const faceVelocity = +(model.face_velocity_actual as number);
  const faceVelocityError =
    faceVelocity > +(model.max_face_velocity as number) ||
    faceVelocity < +(model.min_face_velocity as number);

  // output.tmpl:126-131 — `{{#if model.airflow_actual}}` is a TRUTHINESS test,
  // so a 0 CFM airflow renders the " - - " placeholder, not "0".
  const airflowText = model.airflow_actual
    ? formatIntegerDisplay(model.airflow_actual)
    : '';

  // output.tmpl:312-351 reads the staged section (js:1073 `self.sectionDraft`).
  const seasonId = Number(section.season_id) || null;
  // `{{#if section.season_id}}` + `{{#ifCompare season_id '!=' 2/1}}` — exactly
  // what seasonShowFlags encodes (both false when the season is unset).
  const showSummer = seasonId != null && seasonId !== 2;
  const showWinter = seasonId != null && seasonId !== 1;
  /** output.tmpl:326 / :344 — `{{#if x}}{{get_one_decimal x}}{{else}} -- {{/if}}`
   *  joined by " / ". The ` -- ` fallback carries its surrounding spaces. */
  const ladbValue = (seasonKey: 'summer' | 'winter'): string => {
    const eadb = section[`eadb_temp_${seasonKey}`];
    const eawb = section[`eawb_temp_${seasonKey}`];
    return `${eadb ? getOneDecimal(eadb) : ' -- '} / ${eawb ? getOneDecimal(eawb) : ' -- '}`;
  };
  const seasonRow = (seasonKey: 'summer' | 'winter') => (
    <>
      <div className={`row season-group-${seasonKey}`}>
        <div className="col-xs-12">
          <h4>{seasonKey === 'summer' ? 'Summer' : 'Winter'}</h4>
        </div>
      </div>
      <div className={`row season-group-${seasonKey}`}>
        <div className="col-xs-5 label-part">
          LADB / LAWB
          <span className="unit right temp">F</span>
        </div>
        <div className="col-xs-7 input-part">
          <div className="row">
            <div className="col-xs-6">
              {/* output.tmpl:326 — `disabled` + the inline border/opacity reset;
                  read-only, and NEVER written back to the draft. */}
              <input
                disabled
                style={{ width: '100%', border: 'none', opacity: 1 }}
                type="text"
                autoComplete="off"
                name={`ladb_temp_${seasonKey}`}
                className="filter_section_field search_field"
                value={ladbValue(seasonKey)}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // output.tmpl:5-16 — every <li> keeps `data-name` + its `{{prefix}}Filter_*`
  // marker class, and every <a> a real href + data-toggle (legacy's href is the
  // INPUT pane id; activateOutputLabel, js:1264-1275, appends "_output" itself).
  const navItems: Array<{ tab: OutputTab; label: string }> = [
    { tab: 'filter', label: 'Filter' },
    { tab: 'frame', label: 'Frame' },
    { tab: 'performance', label: 'Performance' },
  ];

  return (
    <>
      <div className="col-xs-1 nav nav-tabs">
        <ul className="col-xs-12 output-menu-items">
          <li className="cursor-default">Output</li>
          {navItems.map(({ tab, label }) => (
            <li
              key={tab}
              className={`output-nav-item${tab === 'frame' && !hasFrame ? ' non-clickable' : ''}${outputTab === tab ? ' selected' : ''} ahu-Filter_${label}`}
              data-name={tab}
              onClick={() => {
                if (tab === 'frame' && !hasFrame) return;
                setOutputTab(tab);
              }}
            >
              {/* without an href the anchor gets the text cursor rather than the
                  pointer — nothing in the cascade sets `cursor` on these. */}
              <a
                data-toggle="tab"
                href={`#ahu-Filter_${label}`}
                onClick={(e) => e.preventDefault()}
              >
                {label}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-xs-11 with-border tab-content filter-rd-output">
        {/* ===== Filter output pane (output.tmpl:21-229) ===== */}
        <div
          className={`output-tab-pane tab-pane fade${outputTab === 'filter' ? ' active in' : ''} scrollable-content  float-left`}
          id="ahu-Filter_Filter_output"
          name="ahu-Filter_Filter_output"
        >
          {/* output.tmpl:23-25 — the Filter pane (and ONLY the Filter pane, cf.
              :232) nests its columns inside `.more-tab-content
              .filter-more-tab-content` > `#filter_properties_output.sub_tab
              .sub_tab_data`. Dropping it cost `margin-top:8px`
              (common.css:24704) and `.more-tab-content > div
              {margin-top:-27px}` (:21188). The inner div keeps `active` because
              `.more-tab-content > div.tab-pane` is display:none (:21191). */}
          <div className="more-tab-content filter-more-tab-content">
            <div
              className="tab-pane fade in active scrollable-content full-height float-left sub_tab sub_tab_data"
              id="filter_properties_output"
            >
              <div className="col-xs-5 col-md-5 col-lg-5 min-width-44">
                {/* output.tmpl:29 / :49 — BARE label text (no <span>), so
                    `.col-xs-5.label-part span {white-space:nowrap}`
                    (common.css:24181) does not apply. */}
                <OutSelect
                  label="Manufacturer"
                  rows={collections.manufacturer}
                  valueId={filterParams.mfg_id}
                />
                <OutSelect
                  label="Series"
                  rows={collections.series}
                  valueId={filterParams.series_id}
                  labelKey="series"
                />
                <OutPair
                  label="Bank Width x Height"
                  unit="in"
                  a={widthNominal}
                  b={heightNominal}
                  aError={!bankWideNormal}
                  bError={!bankHighNormal}
                />
                <OutPair
                  label="Effective Width x Height"
                  unit="in"
                  a={widthNominal}
                  b={model.height}
                  aError={!bankWideNormal}
                  bError={!highNormal}
                />
                {/* output.tmpl:104-113 — the only wrapped-label row without a
                    has_error pair; its input carries the legacy id. */}
                <OutText
                  label={<span>Available Width x Height</span>}
                  unit="in"
                  value={sectionSizes}
                  id="section_aw_width"
                />
                {/* output.tmpl:117-145 — a flex row: the airflow input at a
                    measured px width (calcAirflowWidth, js:1041-1055), a literal
                    comma, then the air-stream <select>. The whole row is
                    non-interactive through `.output-data {pointer-events:none}`.
                    No `readOnly` — see <OutText> below. */}
                <OutRow label="Airflow" unit="CFM">
                  <div className="output-without-unit" style={{ display: 'flex' }}>
                    {airflowText ? (
                      <input
                        tabIndex={-1}
                        type="text"
                        value={airflowText}
                        onChange={() => undefined}
                        style={{ width: `${calcAirflowWidth(airflowText)}px` }}
                      />
                    ) : (
                      <input
                        tabIndex={-1}
                        type="text"
                        value=" - - "
                        onChange={() => undefined}
                        style={{ width: '22px' }}
                      />
                    )}
                    <span style={{ paddingTop: 5 }}>,</span>
                    <OutSelectControl
                      rows={collections.air_stream}
                      valueId={model.air_stream_id}
                    />
                  </div>
                </OutRow>
                <OutText
                  label="Face Area"
                  unit="ft2"
                  value={faceArea !== '' ? Number(faceArea).toFixed(2) : ''}
                />
                {/* output.tmpl:172-173 — `data-type="number"` with NO
                    `data-step`, so validateNumberField (js:1094-1099 →
                    :1427-1430, :1451-1453) rounds it and comma-separates:
                    412.6667 → "413", 1053 → "1,053". */}
                <OutText
                  label="Face Velocity"
                  unit="fpm"
                  value={formatIntegerDisplay(model.face_velocity_actual)}
                  inputClass={faceVelocityError ? 'has_error' : undefined}
                />
                {/* output.tmpl:188 — decimal formatter but NO data-type, so
                    validateNumberField skips it and the raw string prints. */}
                <OutText label="Air PD Initial / Final" unit="in WC" value={apd} />
              </div>
              <ResultTable rows={filtersFrames} modelKey="model" />
            </div>
          </div>
        </div>

        {/* ===== Frame output pane (output.tmpl:230-309) — NO
            `.more-tab-content` wrapper here (:232). ===== */}
        <div
          className={`output-tab-pane tab-pane fade${outputTab === 'frame' ? ' active in' : ''} scrollable-content  float-left`}
          id="ahu-Filter_Frame_output"
          name="ahu-Filter_Frame_output"
        >
          <div className="col-xs-5 col-md-5 col-lg-5 min-width-44">
            <OutSelect
              label="Manufacturer"
              rows={frameCol.manufacturer}
              valueId={frameParams.mfg_id}
            />
            <OutSelect
              label="Series"
              rows={frameCol.series}
              valueId={frameParams.series_id}
              labelKey="series"
            />
          </div>
          <ResultTable rows={filtersFrames} modelKey="frame_model" />
        </div>

        {/* ===== Performance output pane (output.tmpl:310-352) ===== */}
        <div
          className={`output-tab-pane tab-pane fade${outputTab === 'performance' ? ' active in' : ''} scrollable-content float-left`}
          id="ahu-Filter_Performance_output"
          name="ahu-Filter_Performance_output"
        >
          {/* :312 — `data-season="{{section.season_value}}"`; the 50% width /
              15px offset hangs off
              `.tab-pane[id$=Filter_Performance_output] > .col-xs-5`
              (common.css:24801). */}
          <div className="col-xs-5" data-season={String(section.season_value ?? '')}>
            {showSummer && seasonRow('summer')}
            {showWinter && seasonRow('winter')}
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * filter.selector.js:1041-1055 — measure the formatted airflow and add 3px.
 * Legacy reads `window.getComputedStyle(select)`: a bare, undeclared `select`,
 * which resolves to the named global the chassis' `#select` button publishes
 * (ComponentSelectorDialog.tsx renders `id="select"`). Same element here.
 * Legacy measures `formatNumberWithCommaSeparator(raw)`; we measure the string
 * actually displayed (validateNumberField's rounded + comma form) — identical
 * for the integer CFM values this field ever holds.
 */
function calcAirflowWidth(text: string): number {
  const anchor = document.getElementById('select');
  const tempSpan = document.createElement('span');
  tempSpan.style.visibility = 'hidden';
  tempSpan.style.position = 'absolute';
  tempSpan.style.whiteSpace = 'nowrap';
  if (anchor) tempSpan.style.font = window.getComputedStyle(anchor).font;
  tempSpan.textContent = text;
  document.body.appendChild(tempSpan);
  const textWidth = tempSpan.offsetWidth;
  tempSpan.remove();
  return textWidth + 3;
}

/**
 * The output pane's row grammar (output.tmpl:27-46 and its ten siblings). It is
 * NOT the input `FieldRow`: the value column is `col-xs-12 output-data`, and
 * that class is what makes an output field an output field —
 * `#rating_mode_filter_content .output-part .output-data`
 * (common.css:24805-24823) zeroes its padding and then, on the controls,
 * `pointer-events:none; border-bottom:none; appearance:none; height:26px;
 * font-size:13px; text-align:left; padding-left:5px`. Routing these rows
 * through `FieldRow` (a bare `col-xs-12`) left them with an input underline,
 * the wrong height/font, and a live click target.
 *
 * `label` is a ReactNode because legacy wraps it in a <span> for ONLY the
 * Bank / Effective / Available rows (output.tmpl:70, :87, :104) and leaves
 * Manufacturer, Series, Airflow, Face Area, Face Velocity and Air PD as bare
 * text (:29, :49, :119, :148, :165, :181) — the difference decides whether
 * `.col-xs-5.label-part span {white-space:nowrap}` (common.css:24181) applies,
 * i.e. whether "Air PD Initial / Final" may wrap onto two lines.
 */
const OutRow = ({
  label,
  unit,
  children,
}: {
  label: React.ReactNode;
  unit?: string;
  children: React.ReactNode;
}) => (
  <div className="row">
    <div className="col-xs-5 label-part">
      {label}
      {unit && <span className="unit right">{unit}</span>}
    </div>
    <div className="col-xs-7 input-part">
      <div className="row">
        <div className="col-xs-12 output-data">{children}</div>
      </div>
    </div>
  </div>
);

/**
 * output.tmpl:35-41 / :55-62 / :132-140 / :241-248 / :262-270 — a REAL
 * `<select>` over the collection with the current row selected, not an
 * `<input>`. Besides the option list, the element type is what
 * `#rating_mode_filter_content select {font-size:13px; width:calc(100% - 25px)
 * !important}` (common.css:24013) keys off, so the ported input was 25px too
 * wide. Non-interactive via `.output-data {pointer-events:none}`.
 */
const OutSelectControl = ({
  rows,
  valueId,
  labelKey = 'name',
}: {
  rows: Array<Record<string, unknown>> | undefined;
  valueId: unknown;
  labelKey?: string;
}) => {
  const list = rows ?? [];
  const current = valueId == null || valueId === '' ? '' : String(valueId);
  // legacy leaves the `- -` option selected when nothing matches (its `hide`
  // class only removes it from the drop-down list, not from the closed field).
  const value = list.some((r) => String(r.id) === current) ? current : '';
  return (
    <select tabIndex={-1} value={value} onChange={() => undefined}>
      <option className="hide" value="">- -</option>
      {list.map((r) => (
        <option
          key={String(r.id)}
          value={String(r.id)}
          data-id={String(r.id)}
          data-value={String(r[labelKey] ?? '')}
        >
          {String(r[labelKey] ?? '')}
        </option>
      ))}
    </select>
  );
};

const OutSelect = ({
  label,
  rows,
  valueId,
  labelKey,
}: {
  label: React.ReactNode;
  rows: Array<Record<string, unknown>> | undefined;
  valueId: unknown;
  labelKey?: string;
}) => (
  <OutRow label={label}>
    <div className="output-without-unit">
      <OutSelectControl rows={rows} valueId={valueId} labelKey={labelKey} />
    </div>
  </OutRow>
);

/**
 * output.tmpl:111 / :155 / :172 / :188 — a plain `<input>` with NO `readonly`
 * attribute; legacy makes it non-interactive with `tabindex="-1"` plus
 * `#rating_mode_filter_content .output-part .output-data input
 * {pointer-events:none}` (common.css:24808-24809), never with `readonly`.
 * The port added `readOnly`, which pulled in `input:read-only {color:#757575}`
 * (ahu/styles.css:1123) and greyed the Available W x H / Face Area / Face
 * Velocity / Air PD values against dev's default text colour. Dropping the
 * attribute restores the legacy markup exactly — the value stays uneditable
 * through the noop `onChange` (controlled input), `tabIndex={-1}` and
 * `pointer-events:none`, i.e. the same three mechanisms legacy relies on.
 */
const OutText = ({
  label,
  unit,
  value,
  id,
  inputClass,
}: {
  label: React.ReactNode;
  unit?: string;
  value: unknown;
  id?: string;
  inputClass?: string;
}) => (
  <OutRow label={label} unit={unit}>
    <div className="output-without-unit">
      <input
        tabIndex={-1}
        type="text"
        id={id}
        className={inputClass}
        value={value == null ? '' : String(value)}
        onChange={() => undefined}
      />
    </div>
  </OutRow>
);

/**
 * output.tmpl:68-84 (Bank) and :85-101 (Effective). Two things the port lost:
 *  • the value div's class is built as `hide{{#if a}}-{{/if}}{{#if b}}-{{/if}}`,
 *    so with BOTH values absent it is the literal `hide` and
 *    `.output-part .hide {display:none}` (common.css:15095) removes the value
 *    entirely — legacy never prints "- - x - -";
 *  • each half carries `{{#unless …_normal}}has_error{{/unless}}`, red via
 *    `.bank_width_height span.has_error` (common.css:24715-24717).
 * `get_one_decimal` is helpers.js:154-157 — `toFixed(2)`, so "24.00" not "24.0",
 * and the empty string for a falsy value.
 */
const OutPair = ({
  label,
  unit,
  a,
  b,
  aError,
  bError,
}: {
  label: string;
  unit: string;
  a: unknown;
  b: unknown;
  aError: boolean;
  bError: boolean;
}) => (
  <OutRow label={<span>{label}</span>} unit={unit}>
    <div
      className={`output-without-unit bank_width_height hide${a ? '-' : ''}${b ? '-' : ''}`}
    >
      {/* output.tmpl:77-79 / :94-96 put each span on its own line, so HTML
          collapses the indentation to a single space either side of the "x"
          and the spans are `display:inline-block` (common.css:24712), which
          preserves it. JSX drops that whitespace — hence dev3's "96.00x96.00"
          against dev's "96.00 x 96.00". Spaces restored explicitly. */}
      <span className={aError ? 'has_error' : ''}>{a ? getOneDecimal(a) : '- -'}</span>{' '}
      <span>x</span>{' '}
      <span className={bError ? 'has_error' : ''}>{b ? getOneDecimal(b) : '- -'}</span>
    </div>
  </OutRow>
);

/** output.tmpl:195 / :277 — `col-xs-7 col-md-7 col-lg-7 max-width-56`; the port
 *  dropped the md/lg duplicates (harmless at desktop widths, restored for
 *  fidelity). The per-row `wide_normal`/`height_normal` has_error spans legacy
 *  emits at :210-211 / :292-293 are deliberately NOT reproduced: transform_models
 *  (js:1019-1040) never sets those keys, so `{{#unless}}` ALWAYS fires there, and
 *  no rule matches `td .has_error` — it is dead legacy markup. */
const ResultTable = ({ rows, modelKey }: { rows: Array<Record<string, unknown>>; modelKey: string }) => (
  <div className="col-xs-7 col-md-7 col-lg-7 max-width-56">
    <div className="table-wrapper">
      <table>
        <thead>
          <tr>
            <td className="td_model">MODEL</td>
            <td className="td_size">SIZE</td>
            <td className="td_qty">QTY</td>
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((r, i) => (
              <tr key={i}>
                <td className="td_model">{String(r[modelKey] ?? '')}</td>
                <td className="td_size">
                  <span>{String(r.wide ?? '')}W</span> x <span>{String(r.high ?? '')}H</span>
                </td>
                <td className="td_qty">{String(r.qty ?? '')}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td className="td_model" />
              <td className="td_size" />
              <td className="td_qty" />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

/**
 * ★ Portalled to `#ksdm-content`, matching legacy's
 * `$('#ksdm-content').append(this.howToTemplate())` (filter.selector.js:1809).
 * `.filter-how-to-popup .popup-content` is `position:fixed` (common.css:24478),
 * but the selector mounts inside `#component_rating_mode.rating-mode-wrapper` /
 * `.rotate-card`, which carry the board's card-flip `transform: matrix3d(...)`.
 * A transformed ancestor becomes the containing block for `position:fixed`
 * descendants, so an inline render landed the popup at x=1054/y=248 instead of
 * legacy's x=564/y=142 and clipped its right-hand side. (Verified old↔new on
 * dev3; same defect and same fix as the fan how-to.)
 */
const HowToPopup = ({ onClose }: { onClose: () => void }) => createPortal(
  <div className="filter-how-to-popup popup-wrapper active">
    <div className="popup-content floating-popup" id="floating-popup">
      <h2 className="popup-heading">
        <i className="icon-info" />
        How to Select a Filter and Frame
      </h2>
      <div className="popup-content-part filter-how-to-body">
        <p><b>In the AHU Board,</b></p>
        <ul>
          <li>select the filter section in the Canvas model and press ENTER</li>
          <li>select the filter section in the Canvas model and click on SELECTION in the Data Bar</li>
          <li>double-click on the filter section.</li>
        </ul>
        <p><b>In the Filter selection dialog,</b></p>
        <ul>
          <li>click on the Selection tab</li>
          <li>select your air stream and desired</li>
          <li>input your airflow</li>
        </ul>
        <p><b>If your Bank Configuration list is blank and the filter count = 0,</b></p>
        <ul>
          <li>then there are no bank configurations available for the given Airflow and set Conditions.</li>
          <li>in order to obtain valid bank configuration(s) and filters,
            <ul>
              <li>Increase your tunnel cabinet width and/or height, in the Data Bar.</li>
              <li>Reduce your airflow</li>
              <li>In the Mounting menu,
                <ul>
                  <li>Change the filter Arrangement and/or Loading</li>
                </ul>
              </li>
              <li>In the Conditions tab,
                <ul>
                  <li>Increase your maximum face velocity.</li>
                  <li>Change the Dimensional Constraints value from the default &quot;All Configurations that fit&quot; to &quot;All Configurations&quot;.</li>
                  <li>increase the maximum number of sizes</li>
                  <li>select more allowable filter sizes</li>
                </ul>
              </li>
            </ul>
          </li>
        </ul>
        <p><b>If you get Bank Configuration(s) but the filter count = 0,</b></p>
        <ul>
          <li>then there are no filters available for the selected Bank Configuration and set Conditions</li>
          <li>In order to obtain filters,
            <ul>
              <li>Select a different Bank Configuration from the list.</li>
            </ul>
          </li>
        </ul>
        <p><b>If your filter count &gt; 0</b></p>
        <ul>
          <li>Select your Bank Configuration. The system will select the optimal Bank Configuration by default and place it at the top of the list.</li>
          <li>Click on Smartlist and open the list of all filters available for the selected Bank Configuration, Airflow, Mounting Arrangement, Loading and set Conditions.</li>
        </ul>
        <p><b>Remember that..</b></p>
        <ul>
          <li>The filter count may change based on the Bank Configuration, as not all filters are available in all sizes.</li>
          <li>The filter count may change based on the Mounting Arrangement; Flat-Bank or V-Bank.</li>
          <li>V-Bank Arrangement is only available for 1&quot;, 2&quot; and 4&quot; filters.</li>
          <li>By changing the Mounting Angle, you may tilt the entire filter bank at an angle. Option is only available for 1&quot;, 2&quot; and 4&quot; filters.</li>
        </ul>
      </div>
      <div className="popup-control-buttons">
        <button className="filter-how-to-close" name="filter_how_to_close" onClick={onClose}>
          CLOSE
        </button>
      </div>
    </div>
  </div>,
  document.getElementById('ksdm-content') ?? document.body,
);

export default FilterSelector;
